import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import ProfessionalResumeForm from "./ProfessionalResumeForm";
import ProfessionalResumePreview from "./ProfessionalResumePreview";
import SimpleResumeForm from "./SimpleResumeForm";
import SimpleResumePreview from "./SimpleResumePreview";
import ModernResumeForm from "./ModernResumeForm";
import ModernResumePreview from "./ModernResumePreview";
import SecurityResumeForm from "./SecurityResumeForm";
import SecurityResumePreview from "./SecurityResumePreview";
import TechResumeForm from "./TechResumeForm";
import TechResumePreview from "./TechResumePreview";
import PETeacherResumeForm from "./PETeacherResumeForm";
import PETeacherResumePreview from "./PETeacherResumePreview";
import BusinessDevResumeForm from "./BusinessDevResumeForm";
import BusinessDevResumePreview from "./BusinessDevResumePreview";
import ExecutiveResumeForm from "./ExecutiveResumeForm";
import ExecutiveResumePreview from "./ExecutiveResumePreview";
import MarketingSpecialistResumeForm from "./MarketingSpecialistResumeForm";
import MarketingSpecialistResumePreview from "./MarketingSpecialistResumePreview";
import ScienceTeacherResumeForm from "./ScienceTeacherResumeForm";
import ScienceTeacherResumePreview from "./ScienceTeacherResumePreview";
import EnglishTeacherResumeForm from "./EnglishTeacherResumeForm";
import EnglishTeacherResumePreview from "./EnglishTeacherResumePreview";
import ConsultingProfessionalResumeForm from "./ConsultingProfessionalResumeForm";
import ConsultingProfessionalResumePreview from "./ConsultingProfessionalResumePreview";
import TCSResumeForm from "./TCSResumeForm";
import TCSResumePreview from "./TCSResumePreview";
import ATSResumePreview from "./ATSResumePreview";
import ResumeUploadModal from "./ResumeUploadModal";
import "./react-template.css";
import "./resume-styles.css";
import { normalizeArray, normalizeText } from "./utils/normalize";

const SAVED_TEMPLATES_STORAGE_KEY = "savedCvTemplates";

const formatTemplateLabel = (id = "") => {
  if (!id) return "Unknown Template";
  return id
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const cloneResumeData = (data) => {
  if (!data) return data;
  try {
    return JSON.parse(JSON.stringify(data));
  } catch (error) {
    console.error("Failed to clone resume data", error);
    return data;
  }
};

const normalizeSkillList = (list = []) =>
  Array.from(
    new Set(
      list
        .map((item) => normalizeText(item))
        .filter(Boolean)
    )
  );

const extractExperienceBullets = (exp) => {
  if (!exp) return [];
  const source =
    exp.description_points ??
    exp.duties ??
    exp.responsibilities ??
    exp.summary ??
    exp.description ??
    [];
  const bullets = normalizeArray(source);
  return bullets.length ? bullets : [];
};

const getLegacyTemplateData = (templateId) => {
  if (templateId === "ats" || templateId === "tcs") {
    return null;
  }
  let initialData = {};
  if (templateId === "simple") {
    initialData = {
      template_name: "simple",
      isTemplate: true,
      full_name: "YOUR NAME",
      address: "Your Address 222\nAnywhere St., Any City, ST 2222",
      phone: "+1-222-222-2222",
      email: "your_email@email.com",
      highlights: [
        "Results-Oriented Professional: Proven track record of achieving measurable results and exceeding goals in [industry/field].",
        "Technical Proficiency: Proficient in Microsoft Office Suite, [other relevant software/tools], with a strong aptitude for learning new technologies.",
        "Adaptable and Resourceful: Flexible and adaptable in fast-paced environments, with the ability to prioritize tasks and meet deadlines.",
      ],
      work_experience: [
        {
          company: "Company Name",
          location: "Location",
          job_title: "Job Title",
          start_date: "Month, Year",
          end_date: "Present",
          key_responsibilities: "Key Responsibilities",
          duties: [
            "Collaborated closely with cross-functional teams to streamline processes and optimize workflow efficiency.",
            "Conducted market research and analysis to identify emerging trends and opportunities, providing valuable insights for strategic decision-making.",
            "Maintained accurate records and documentation, ensuring compliance with company policies and regulatory requirements",
          ],
        },
      ],
      education: [
        {
          degree: "Degree in Major/Field of Study",
          institute: "Name of Institution",
          location: "Location",
          start_year: "2020",
          end_year: "2024",
        },
      ],
      professional_summary:
        "Results-oriented professional with [number] years of experience in [industry/field]. Skilled in [mention key skills/qualities]. Strong [communication/problem-solving/other relevant skills]. Seeking to leverage experience and expertise into [desired position].",
      additional_sections: {
        certifications: ["List of relevant certifications"],
        awards_honors: ["Remembrance/Honor received"],
        volunteer_experience: ["Volunteer Activities"],
        professional_affiliations: ["Member of department/organization"],
      },
    };
  } else if (templateId === "modern") {
    initialData = {
      template_name: "modern",
      isTemplate: true,
      profile_image_url: "",
      full_name: "RICHARD SWART",
      job_title: "MARKETING MANAGER",
      email: "hello@reallygreatsite.com",
      phone: "+123-456-7890",
      location: "123 Anywhere St., Any City",
      website: "www.reallygreatsite.com",
      professional_summary:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.",
      professional_skills: [
        "Project Management",
        "Public Relations",
        "Teamwork",
        "Time Management",
        "Leadership",
        "Effective Communication",
        "Critical Thinking",
      ],
      technical_skills: [
        "Microsoft Office Suite",
        "Google Analytics",
        "Social Media Management",
        "Email Marketing Tools",
      ],
      education: [
        {
          degree: "Bachelor's Degree in Marketing",
          institution: "University of Business",
          location: "New York, NY",
          start_date: "2020",
          graduation_date: "2024",
        },
      ],
      experience: [
        {
          company: "Borcelle Studio",
          position: "Marketing Manager & Specialist",
          start_date: "2030",
          end_date: "PRESENT",
          description_points: [
            "Develop and execute comprehensive marketing strategies and campaigns that align with the company's goals and objectives.",
            "Lead, mentor, and manage a high-performing marketing team, fostering a collaborative and results-driven work environment.",
            "Monitor brand consistency across marketing channels and materials.",
          ],
        },
        {
          company: "Fauget Studio",
          position: "Marketing Manager & Specialist",
          start_date: "2025",
          end_date: "2029",
          description_points: [
            "Create and manage the marketing budget, ensuring efficient allocation of resources and maximizing ROI.",
            "Oversee market research to identify emerging trends, customer needs, and competitive strategies.",
            "Monitor brand consistency across marketing channels and materials.",
          ],
        },
        {
          company: "Studio Shodwe",
          position: "Marketing Manager & Specialist",
          start_date: "2024",
          end_date: "2025",
          description_points: [
            "Develop and maintain strong relationships with partners, agencies, and vendors to support marketing initiatives.",
            "Monitor and maintain brand consistency across all marketing channels and materials.",
          ],
        },
      ],
      projects: [
        {
          title: "Digital Marketing Campaign",
          description:
            "Led successful digital marketing campaign resulting in 40% increase in customer engagement",
          link: "https://example.com/project",
          technologies: ["Google Analytics", "Social Media", "Email Marketing"],
        },
      ],
      achievements: [
        "Increased client acquisition by 40% through strategic marketing campaigns",
        "Led team of 5 marketing specialists to achieve 25% ROI improvement",
        "Managed $500K marketing budget successfully",
      ],
      certifications: [
        { name: "Google Analytics Certification", provider: "Google" },
        {
          name: "Digital Marketing Professional Certificate",
          provider: "Digital Marketing Institute",
        },
        { name: "Project Management Professional (PMP)", provider: "PMI" },
      ],
      languages: [
        { name: "English", proficiency: "Fluent" },
        { name: "French", proficiency: "Fluent" },
        { name: "German", proficiency: "Basics" },
        { name: "Spanish", proficiency: "Intermediate" },
      ],
      references: [
        {
          name: "Estelle Darcy",
          title: "CTO",
          company: "Wardiere Inc.",
          phone: "123-456-7890",
          email: "hello@reallygreatsite.com",
        },
        {
          name: "Harper Richard",
          title: "CEO",
          company: "Wardiere Inc.",
          phone: "123-456-7890",
          email: "hello@reallygreatsite.com",
        },
      ],
    };
  } else if (templateId === "security-manager") {
    initialData = {
      template_name: "security-manager",
      isTemplate: true,
      profile_image_url: "",
      full_name: "TERRENCE MCKINNEY",
      job_title: "HOTEL SECURITY MANAGER",
      email: "hello@reallygreatsite.com",
      phone: "+123-456-7890",
      location: "123 Anywhere St., Any City",
      linkedin: "linkedin.com/in/terrencemckinney",
      professional_summary:
        "Detail-oriented and proactive Hotel Security Manager with a proven track record of ensuring the safety and security of guests, staff, and property. Skilled in developing and implementing comprehensive security protocols, managing security personnel, and utilizing advanced surveillance technology. Adept at crisis management and emergency response, with a strong commitment to maintaining a secure and welcoming environment.",
      professional_skills: [
        "Security Operations Management",
        "Surveillance & CCTV Monitoring",
        "Access Control Systems",
        "Emergency Response & First Aid",
        "Risk Assessment & Mitigation",
        "Team Leadership & Training",
        "Report Writing & Documentation",
        "Conflict Resolution & De-escalation",
      ],
      soft_skills: [
        "Attention to Detail",
        "Problem-Solving",
        "Communication",
        "Integrity & Trustworthiness",
      ],
      education: [
        {
          degree: "Bachelor's Degree in Criminal Justice",
          institute: "University of Security Studies",
          location: "Chicago, IL",
          start_year: "2018",
          end_year: "2022",
        },
      ],
      work_experience: [
        {
          position: "Hotel Security Manager",
          company: "Grand Astoria Hotel",
          start_date: "2028",
          end_date: "PRESENT",
          responsibilities: [
            "Oversee daily security operations, including staff scheduling, patrols, and incident response.",
            "Develop and enforce security policies and procedures to protect hotel assets and ensure guest safety.",
            "Conduct regular security audits and risk assessments to identify and address vulnerabilities.",
            "Train and supervise a team of security officers, providing ongoing guidance and performance evaluations.",
          ],
        },
        {
          position: "Assistant Security Manager",
          company: "The Royal Crest Hotel",
          start_date: "2024",
          end_date: "2028",
          responsibilities: [
            "Assisted in managing the hotel's security program and staff.",
            "Monitored surveillance systems and investigated security breaches.",
            "Responded to emergencies and coordinated with local law enforcement.",
            "Prepared detailed incident reports and maintained security records.",
          ],
        },
      ],
      languages: ["English (Native)", "Spanish (Conversational)"],
    };
  } else if (templateId === "computer-teacher") {
    initialData = {
      template_name: "computer-teacher",
      isTemplate: true,
      full_name: "Benjamin Anderson",
      job_title: "High School Computer Science Teacher",
      email: "support@resumedesign.ai",
      phone: "(555) 345-6789",
      location: "New York, US",
      linkedin: "LinkedIn.com",
      professional_summary:
        "Highly skilled and dedicated High School Computer Science Teacher with 12 years of experience. Adept at creating engaging lesson plans and fostering a dynamic learning environment. Recognized for consistently achieving exceptional student outcomes, with 95% of students passing AP Computer Science exams. Proven track record of integrating technology into curriculum to enhance student understanding and critical thinking skills. Passionate about inspiring the next generation of computer scientists.",
      structured_skills: {
        programming_languages: ["Java", "Python", "C++"],
        tools: ["SQL", "JavaScript", "Algorithms", "Data Structures"],
        frameworks: [
          "Cybersecurity",
          "Networking",
          "Web Development",
          "Database Management",
          "Software Engineering",
        ],
        ai_ml_technologies: [],
      },
      key_skills: [
        "Java",
        "Python",
        "C++",
        "SQL",
        "JavaScript",
        "Algorithms",
        "Data Structures",
        "Cybersecurity",
        "Networking",
        "Web Development",
        "Database Management",
        "Software Engineering",
      ],
      soft_skills: ["Adaptability", "Problem-solving", "Creativity", "Collaboration"],
      work_experience: [
        {
          position: "Senior Computer Science Teacher",
          company: "City High School",
          start_date: "Aug 2009",
          end_date: "Present",
          responsibilities: [
            "Developed and implemented curriculum for Advanced Placement Computer Science course",
            "Provided mentorship and guidance to junior computer science teachers",
            "Organized and led coding clubs and hackathons for students",
            "Implemented project-based learning approach to enhance student engagement and understanding",
            "Achieved a 95% passing rate for students taking the AP Computer Science exam",
          ],
        },
        {
          position: "Junior Computer Science Teacher",
          company: "Town Middle School",
          start_date: "Sep 2007",
          end_date: "Aug 2009",
          responsibilities: [
            "Assisted in developing computer science curriculum for middle school students",
            "Provided one-on-one support to students struggling with coding concepts",
            "Organized and supervised computer science fairs and competitions",
            "Collaborated with other teachers to integrate technology into various subjects",
          ],
        },
      ],
      education: [
        {
          degree: "Masters in Computer Science",
          institution: "Stanford University",
          start_date: "Aug 2008",
          end_date: "May 2010",
          description:
            "Advanced studies in computer science with a focus on programming languages, algorithms, and software development.",
        },
        {
          degree: "Bachelors in Computer Science",
          institution: "University of California, Berkeley",
          start_date: "Aug 2004",
          end_date: "May 2008",
          description:
            "Fundamental coursework in computer science including programming, data structures, and computer systems.",
        },
      ],
      languages: ["English", "Spanish"],
    };
  } else if (templateId === "tech-resume") {
    initialData = {
      template_name: "tech-resume",
      isTemplate: true,
      full_name: "Benjamin Anderson",
      job_title: "High School Computer Science Teacher",
      email: "support@resumedesign.ai",
      phone: "(555) 345-6789",
      location: "New York, US",
      linkedin: "LinkedIn.com",
      professional_summary:
        "Highly skilled and dedicated High School Computer Science Teacher with 12 years of experience. Adept at creating engaging lesson plans and fostering a dynamic learning environment. Recognized for consistently achieving exceptional student outcomes, with 95% of students passing AP Computer Science exams. Proven track record of integrating technology into curriculum to enhance student understanding and critical thinking skills. Passionate about inspiring the next generation of computer scientists.",
      skills: {
        programming_languages: ["Java", "Python", "C++"],
        tools: ["SQL", "JavaScript", "Algorithms", "Data Structures"],
        frameworks: [
          "Cybersecurity",
          "Networking",
          "Web Development",
          "Database Management",
          "Software Engineering",
        ],
        ai_ml_technologies: [],
      },
      key_skills: [
        "Java",
        "Python",
        "C++",
        "SQL",
        "JavaScript",
        "Algorithms",
        "Data Structures",
        "Cybersecurity",
        "Networking",
        "Web Development",
        "Database Management",
        "Software Engineering",
      ],
      soft_skills: ["Adaptability", "Problem-solving", "Creativity", "Collaboration"],
      relevant_experience: [
        {
          position: "Senior Computer Science Teacher",
          company: "City High School",
          start_date: "Aug 2009",
          end_date: "Present",
          location: "New York, US",
          description_points: [
            "Developed and implemented curriculum for Advanced Placement Computer Science course",
            "Provided mentorship and guidance to junior computer science teachers",
            "Organized and led coding clubs and hackathons for students",
            "Implemented project-based learning approach to enhance student engagement and understanding",
            "Achieved a 95% passing rate for students taking the AP Computer Science exam",
          ],
        },
        {
          position: "Junior Computer Science Teacher",
          company: "Town Middle School",
          start_date: "Sep 2007",
          end_date: "Aug 2009",
          location: "New York, US",
          description_points: [
            "Assisted in developing computer science curriculum for middle school students",
            "Provided one-on-one support to students struggling with coding concepts",
            "Organized and supervised computer science fairs and competitions",
            "Collaborated with other teachers to integrate technology into various subjects",
          ],
        },
      ],
      education: [
        {
          degree: "Masters in Computer Science",
          institute: "Stanford University",
          location: "Stanford, CA",
          start_year: "2008",
          end_year: "2010",
          description:
            "Advanced studies in computer science with a focus on programming languages, algorithms, and software development.",
        },
        {
          degree: "Bachelor of Science in Computer Science",
          institute: "University of California, Berkeley",
          location: "Berkeley, CA",
          start_year: "2004",
          end_year: "2008",
          description:
            "Fundamental coursework in computer science including programming, data structures, and computer systems.",
        },
      ],
      projects: [
        {
          title: "Student Grade Management System",
          description: [
            "Developed a web-based application for tracking student grades and progress",
            "Implemented using Java Spring Boot and MySQL database",
            "Features include grade calculation, progress tracking, and parent notifications",
          ],
          tech_stack: ["Java", "Spring Boot", "MySQL", "JavaScript", "HTML/CSS"],
          github_link: "https://github.com/benanderson/grade-management",
        },
        {
          title: "Coding Challenge Platform",
          description: [
            "Created an online platform for students to practice coding problems",
            "Includes automated testing and scoring system",
            "Supports multiple programming languages",
          ],
          tech_stack: ["Python", "Django", "Docker", "PostgreSQL"],
          github_link: "https://github.com/benanderson/coding-platform",
        },
      ],
      certifications: [
        "Certified Computer Science Teacher (CSTA)",
        "Google for Education Certified Trainer",
        "Microsoft Certified Educator",
        "AWS Educate Cloud Ambassador",
      ],
      achievements: [
        "Achieved 95% passing rate for AP Computer Science students",
        "Led school to win regional coding competition 3 years in a row",
        "Developed curriculum adopted by 15+ schools in the district",
        "Mentored 20+ new computer science teachers",
      ],
      languages: ["English", "Spanish"],
    };
  } else if (templateId === "pe-teacher") {
    initialData = {
      template_name: "pe-teacher",
      isTemplate: true,
      full_name: "OLIVIA WILSON",
      job_title: "MIDDLE SCHOOL PHYSICAL EDUCATION TEACHER",
      email: "hello@reallygreatsite.com",
      phone: "123-456-7890",
      address: "123 Anywhere St., Any City, ST 12345",
      linkedin: "linkedin.com/in/oliviawilson",
      professional_summary:
        "Enthusiastic and dedicated Middle School Physical Education Teacher with a strong commitment to promoting lifelong fitness and wellness. Skilled in creating inclusive and engaging lesson plans that cater to diverse student abilities and interests. Passionate about fostering a positive and supportive environment where students can develop physical literacy, teamwork, and sportsmanship.",
      work_experience: [
        {
          position: "Physical Education Teacher",
          company: "Westwood Middle School",
          start_date: "AUG 2026",
          end_date: "PRESENT",
          responsibilities: [
            "Develop and implement a dynamic physical education curriculum for grades 6-8.",
            "Teach a variety of sports, fitness activities, and health concepts.",
            "Organize and coach after-school sports teams, including soccer and basketball.",
            "Assess student performance and provide constructive feedback to support their development.",
          ],
        },
        {
          position: "Summer Camp Counselor",
          company: "Pine Ridge Sports Camp",
          start_date: "JUN 2024",
          end_date: "AUG 2026",
          responsibilities: [
            "Led and supervised groups of campers in various sports and recreational activities.",
            "Ensured a safe and positive environment for all participants.",
            "Taught fundamental skills and rules for a wide range of sports.",
          ],
        },
      ],
      education: [
        {
          degree: "Bachelor of Science in Physical Education",
          institution: "State University",
          date: "MAY 2026",
          details: "Minor in Health Education",
        },
      ],
      certifications: [
        "State Teaching License, Physical Education (K-12)",
        "First Aid and CPR/AED Certified",
        "Certified Personal Trainer (ACE)",
      ],
      skills: [
        "Curriculum Design",
        "Sports Coaching",
        "Fitness Assessment",
        "Classroom Management",
        "Student Motivation",
        "Safety & Risk Management",
      ],
    };
  } else if (templateId === "business-development") {
    initialData = {
      template_name: "business-development",
      isTemplate: true,
      full_name: "TheCVGuy.net",
      email: "thecvguy@gmail.com",
      phone: "+88 01817110000",
      location: "Dhaka, Bangladesh",
      websites: ["in/thecvguy.net/"],
      professional_summary:
        "Results-driven Manager with over [insert years of experience] years of proven success in Business Development. Adept at driving revenue growth, forging strategic partnerships, and leading high-performing teams. Demonstrated ability to diversify product portfolios and achieve remarkable revenue milestones, including a 50% annual growth rate. Experienced in market analysis, budget management, and cross-functional collaboration. Known for establishing and nurturing key client and partner relationships.",
      relevant_experience: [
        {
          position: "Manager, Business Development",
          company: "bKash Ltd.",
          start_date: "January 2016",
          end_date: "Present",
          achievements: [
            "Implemented and executed a strategic plan that resulted in a remarkable 50% increase in annual revenue, reaching [specify the revenue figure] within [mention the relevant time frame].",
            "Successfully negotiated and established strategic partnerships with [mention key partners], expanding the company's market reach and contributing to [specify percentage] of annual revenue.",
            "Oversaw a dynamic business development team of [mention team size], achieving departmental targets consistently for the past [mention the number of years].",
          ],
          description_points: [
            "Develop and execute comprehensive business development strategies to drive revenue growth, enhance market penetration, and maximize profitability.",
            "Identify and evaluate new market opportunities and trends to create data-driven business expansion plans.",
            "Cultivate and maintain strong relationships with key partners, clients, and stakeholders.",
            "Lead cross-functional teams to develop and launch new products and services in alignment with market demands.",
            "Collaborate with the finance team to prepare and manage budgets, ensuring resource allocation aligns with strategic priorities.",
          ],
        },
        {
          position: "Executive, Business Development",
          company: "BRAC",
          start_date: "Jan 2014",
          end_date: "Dec 2015",
          achievements: [
            "Successfully identified new market segments and strategic partnerships, resulting in a 30% boost in annual revenue for the organization.",
            "Spearheaded negotiations and secured contracts worth $2 million with key clients, demonstrating exceptional sales and communication skills.",
            "Devised and executed a comprehensive market expansion strategy, leading to the establishment of five new business locations within the first year.",
          ],
          description_points: [
            "Conducted market research to identify emerging trends and opportunities, enabling data-driven decision-making for the organization.",
            "Collaborated with cross-functional teams to develop and launch new products and services, resulting in a diversified revenue stream.",
            "Prepared and delivered compelling sales presentations to potential clients, effectively communicating the value proposition of BRAC's offerings.",
            "Developed and maintained a robust CRM system, ensuring accurate tracking of leads, sales, and customer interactions.",
            "Assisted in the development of annual business development budgets and financial forecasts, contributing to sound financial planning and resource allocation.",
          ],
        },
      ],
      education: [
        {
          degree: "MBA in Marketing",
          end_year: "2018",
          institution: "North South University",
          cgpa: "3.50 (out of 4.00)",
        },
        {
          degree: "BBA in Marketing",
          end_year: "2013",
          institution: "Bangladesh University of Business & Technology",
          cgpa: "3.20 (out of 4.00)",
        },
      ],
      key_skills: [
        "Business Development",
        "Market Research",
        "Market research and analysis",
        "Negotiation Skills",
        "Client Relationship Management",
        "Data-Driven Decision Making",
        "Cross-Functional Collaboration",
        "Conflict Resolution",
        "Project Management",
      ],
      tools_tech: [
        "MS Office",
        "CRM Software",
        "CMS",
        "CRM",
        "SEO Tools",
        "Google Analytics",
        "Google Docs",
      ],
      training: [
        {
          title: "Accelerate Your Career with Emotional Intelligence",
          year: "2022",
          institute: "Institute Name",
          description:
            "Developed a firm grasp of emotional intelligence in this course, understanding its significance, particularly in leadership and conflict management, surpassing 10+ hours.",
        },
        {
          title: "Your English Communication Skills Specialization",
          year: "2020",
          institute: "Institute Name",
          description:
            "Enhanced English communication, both written and spoken, achieving an excellent level of proficiency.",
        },
      ],
      languages: [
        { name: "BENGALI", proficiency: 100 },
        { name: "ENGLISH", proficiency: 85 },
      ],
      reference: {
        name: "Demo Name",
        designation: "Demo Designation",
        company: "Demo Company Name",
        email: "[Email Address]",
        phone: "[Phone Number]",
      },
      projects: [],
    };
  } else if (templateId === "cv-guy") {
    initialData = {
      template_name: "cv-guy",
      isTemplate: true,
      full_name: "JONATHAN DOE",
      job_title: "Senior Project Manager",
      address: "123 Anywhere St., Any City, ST 12345",
      phone: "123-456-7890",
      email: "hello@reallygreatsite.com",
      linkedin: "linkedin.com/in/jonathandoe",
      summary:
        "A highly accomplished and results-oriented Senior Project Manager with over 15 years of experience in leading complex projects from conception to completion. Proven ability to manage cross-functional teams, control budgets, and deliver projects on time and within scope. Expertise in Agile and Waterfall methodologies, with a strong focus on stakeholder communication and risk management. Seeking to leverage extensive experience to drive success in a challenging new role.",
      professional_summary:
        "A highly accomplished and results-oriented Senior Project Manager with over 15 years of experience in leading complex projects from conception to completion. Proven ability to manage cross-functional teams, control budgets, and deliver projects on time and within scope. Expertise in Agile and Waterfall methodologies, with a strong focus on stakeholder communication and risk management. Seeking to leverage extensive experience to drive success in a challenging new role.",
      experience: [
        {
          position: "Senior Project Manager",
          company: "Tech Solutions Inc.",
          start_date: "2018",
          end_date: "Present",
          responsibilities: [
            "Led a portfolio of IT projects with budgets up to $5 million, consistently delivering on or ahead of schedule.",
            "Managed a team of 15 project managers and analysts, providing mentorship and performance oversight.",
            "Implemented a new Agile framework that improved team productivity by 25%.",
            "Served as the primary point of contact for key stakeholders, ensuring clear communication and alignment.",
          ],
        },
        {
          position: "Project Manager",
          company: "Creative Minds Agency",
          start_date: "2012",
          end_date: "2018",
          responsibilities: [
            "Managed digital marketing campaigns for Fortune 500 clients, from initial brief to final delivery.",
            "Coordinated with creative, technical, and account teams to ensure project success.",
            "Successfully delivered over 50 projects, resulting in high client satisfaction and repeat business.",
          ],
        },
      ],
      education: [
        {
          degree: "Project Management Professional (PMP)",
          institution: "Project Management Institute",
          date: "2015",
        },
        {
          degree: "Bachelor of Arts in Business Administration",
          institution: "University of Excellence",
          date: "2012",
        },
      ],
      skills: [
        "Project Management (Agile & Waterfall)",
        "Budgeting & Cost Control",
        "Risk Management",
        "Stakeholder Communication",
        "Team Leadership & Mentorship",
        "Quality Assurance",
      ],
      key_skills: [
        "Project Management (Agile & Waterfall)",
        "Budgeting & Cost Control",
        "Risk Management",
        "Stakeholder Communication",
        "Team Leadership & Mentorship",
        "Quality Assurance",
      ],
      websites: [],
      projects: [
        {
          title: "Excepteur sint occaecat cupidatat non proident",
          description: [
            "Excepteur sint occaecat cupidatificia deserunt mollit anim data preprocessing.",
          ],
          tech_stack: ["Project Management", "Data Analysis"],
          github_link: "",
        },
        {
          title: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
          description: [
            "Duis aute irure dolor in reprehenderit in voluptate",
            "Velit esse cillum dolore eu fugiat nulla pariatur.",
          ],
          tech_stack: ["Strategic Planning", "Process Improvement"],
          github_link: "",
        },
        {
          title:
            "Excepteur sint proident, sunt in culpa qui officia deserunt mollit anim",
          description: [
            "Excepteur sint occaecat cupidatificia deserunt mollit anim data preprocessing.",
          ],
          tech_stack: ["Leadership", "Change Management"],
          github_link: "",
        },
      ],
      certifications: [
        "Become a Data analyst | AI Quest",
        "Data Visualization: Empowering Business with Effective Insights | Forage",
        "Data annotation: Image and Video | Quantigo AI",
      ],
      achievements: [],
    };
  } else if (templateId === "marketing-specialist") {
    initialData = {
      template_name: "marketing-specialist",
      isTemplate: true,
      profile_image_url: "",
      full_name: "JASMINE CLEDANOR",
      phone: "239-206-0560",
      email: "jasnor223@gmail.com",
      location: "Connecticut",
      professional_summary:
        "Creative and passionate graduate with a talent for blending digital storytelling with influential content to deliver marketing campaigns that drive engagement and growth across social channels. Utilize complex analytics and data to identify audience expectations and build messaging that communicates value and fosters meaningful connections. Collaborative relationship builder and team player seeking an opportunity to blend academic knowledge with professional experience to deliver digital and social media goals while enhancing brand awareness.",
      education: [
        {
          degree: "Bachelor's Degree in Business Administration (Marketing Major)",
          institution: "Florida International University",
          graduation_date: "2023",
          key_coursework:
            "Social Media Marketing, Digital Marketing, Consumer Behavior, Business Statistics & Analysis, Organizational Management, Social Media Campaign Management",
          certificate: "Digital Marketing & Social Media Analytics",
        },
        {
          degree: "Associates Degree",
          institution: "Florida SouthWestern State College",
          graduation_date: "2022",
          key_coursework:
            "Principles of Macroeconomics & Microeconomics, Business Finance, Computer Applications for Business",
        },
      ],
      key_skills: [
        "Digital Marketing Management",
        "Social Media Management",
        "Campaign Creation & Optimization",
        "Content Development & Digital Storytelling",
        "Critical & Analytical Thinking",
        "Market & Data Analysis",
        "Planning, Organization & Prioritization",
        "Strategy Planning & Implementation",
        "Creativity",
        "Collaboration & Teamworking",
        "Detail & Deadline-oriented",
        "Visual Communication & Presentation",
      ],
      technical_skills: [
        "Outlook",
        "HubSpot",
        "Hootsuite",
        "Tableau",
        "Canva",
        "Adobe Photoshop",
        "MailChimp",
        "ActiveCampaign",
        "Google Analytics",
        "SEMrush",
      ],
      relevant_experience: [
        {
          position: "Digital Marketing Social Media Intern",
          company: "Sweet High Media",
          location: "Connecticut",
          start_date: "2022",
          end_date: "2023",
          description_points: [
            "Increased engagement and followers across the TikTok, Instagram, Facebook, YouTube, and Pinterest accounts of multiple singers, actors, dancers, and content creators.",
            "Analyzed previous posts to inform the creation of relevant and interesting content that catered to Gen Z and aligned with the unique voice of each account",
            "Identified and employed trending sounds and relevant hashtags to maximize traction and elevate the chances of content going viral",
            "Implemented a campaign for up-and-coming singer Jena Rose, embedding her latest songs within social media content (Instagram videos and TikToks) and partnering with influencers to maximize reach",
            "Leveraged analytical tools (Google Analytics) and data to track marketing campaign performance and growth, while utilizing insights to build, launch, and improve social media strategies",
            "Supported Sweet High Media's Grammy coverage by publishing nominee interviews across social media channels",
            "Conducted research into both the demographic and psychographic information and profiles of the target audience by conducting surveys, analyzing competitor audiences, and executing A/B testing",
            "Deployed SEO strategies to elevate company page rankings and position content for improved engagement",
            "Crafted on-brand emails that played a key part in email marketing campaigns to increase website traffic and followers while enhancing brand awareness",
          ],
        },
      ],
      projects: [],
      certifications: [],
      achievements: [],
    };
  } else if (templateId === "science-teacher") {
    initialData = {
      template_name: "science-teacher",
      isTemplate: true,
      full_name: "SARAH CHEN",
      job_title: "Elementary Science Teacher",
      phone: "123-456-7890",
      email: "hello@reallygreatsite.com",
      linkedin: "linkedin.com/in/sarahchen",
      address: "123 Anywhere St., Any City, ST 12345",
      summary:
        "Passionate and innovative Elementary Science Teacher with a talent for making science accessible and exciting for young learners. Dedicated to creating hands-on, inquiry-based learning experiences that foster curiosity and critical thinking. Skilled in curriculum development, classroom management, and integrating technology to enhance instruction. Committed to building a positive and inclusive classroom where all students can thrive.",
      skills: [
        "Curriculum Development",
        "Inquiry-Based Learning",
        "Hands-On Experimentation",
        "Classroom Management",
        "Educational Technology",
        "Student Assessment",
      ],
      experience: [
        {
          position: "Science Teacher (Grades 3-5)",
          company: "Lakeside Elementary School",
          start_date: "2020",
          end_date: "Present",
          responsibilities: [
            "Developed and taught an engaging science curriculum aligned with state standards.",
            "Organized an annual school-wide science fair to showcase student projects.",
            "Led an after-school STEM club for students interested in advanced topics.",
            "Collaborated with colleagues to create interdisciplinary learning units.",
          ],
        },
        {
          position: "Teaching Assistant",
          company: "Oakwood Elementary School",
          start_date: "2018",
          end_date: "2020",
          responsibilities: [
            "Assisted lead teachers in science and math classrooms.",
            "Provided one-on-one support to students with diverse learning needs.",
            "Helped prepare materials for hands-on activities and experiments.",
          ],
        },
      ],
      education: [
        {
          degree: "Master of Arts in Teaching, Elementary Education",
          institution: "Rivertown University",
          date: "2020",
        },
        {
          degree: "Bachelor of Science in Biology",
          institution: "State College",
          date: "2018",
        },
      ],
      certifications: [
        "State Teaching License, Elementary Education (K-6)",
        "STEM Education Certificate",
      ],
    };
  } else if (templateId === "english-teacher") {
    initialData = {
      template_name: "english-teacher",
      isTemplate: true,
      full_name: "Scarlett Jackson",
      email: "support@resumedesign.ai",
      phone: "(555) 345-6789",
      location: "New York, US",
      linkedin: "LinkedIn.com",
      professional_summary:
        "Passionate and dedicated High School English Teacher with 13 years of experience in creating engaging lesson plans and fostering a love for literature in students. Proven track record of improving standardized test scores by 20% through innovative teaching methods. Recognized for implementing a successful after-school writing program that resulted in a 30% increase in student participation. Skilled in creating a positive and inclusive classroom environment that promotes critical thinking and creativity. Strong communication skills and ability to collaborate effectively with colleagues to enhance student learning outcomes.",
      key_skills: [
        "Literature",
        "Writing",
        "Grammar",
        "Communication",
        "Classroom management",
        "Lesson planning",
        "Critical thinking",
        "Analytical reading",
        "Public speaking",
        "Team collaboration",
      ],
      languages: ["English", "Spanish"],
      relevant_experience: [
        {
          position: "High School English Teacher",
          company: "ABC High School",
          location: "New York, NY",
          start_date: "Aug 2008",
          end_date: "Jun 2021",
          description_points: [
            "Planned and delivered engaging English lessons for grades 9-12",
            "Provided constructive feedback on student writing assignments",
            "Organized and supervised extracurricular activities and clubs",
            "Collaborated with colleagues to develop cross-curricular projects",
            "Achieved a 95% pass rate for standardized English assessments",
          ],
        },
        {
          position: "Lead High School English Teacher",
          company: "XYZ High School",
          location: "New York, NY",
          start_date: "Aug 2014",
          end_date: "Present",
          description_points: [
            "Mentored and coached junior English teachers in best practices",
            "Implemented technology tools to enhance student learning experience",
            "Developed differentiated instruction techniques for diverse learners",
            "Led professional development workshops for faculty members",
            "Increased student participation in English literature events by 20%",
          ],
        },
      ],
      education: [
        {
          degree: "Masters in Education",
          institution: "University of Education",
          location: "New York, NY",
          start_year: "2009",
          end_year: "2011",
          description:
            "Focused on advanced teaching techniques and curriculum development.",
        },
        {
          degree: "Bachelors in English Literature",
          institution: "State College",
          location: "New York, NY",
          start_year: "2005",
          end_year: "2009",
          description:
            "Studied various genres of literature and honed writing skills.",
        },
      ],
      skills: {
        english_subjects: ["Literature", "Writing", "Grammar", "Communication"],
        teaching_methods: [
          "Classroom management",
          "Lesson planning",
          "Critical thinking",
          "Analytical reading",
        ],
        literature_knowledge: ["Public speaking", "Team collaboration"],
        technology_tools: [],
      },
      certifications: [
        "State Teaching License, English (7-12)",
        "Advanced Placement Literature Certification",
      ],
      achievements: [
        "Improved standardized test scores by 20% through innovative teaching methods",
        "Implemented successful after-school writing program with 30% student participation increase",
        "Led English department with 15 teachers under supervision",
        "Organized school-wide literacy events and book clubs",
      ],
    };
  } else if (templateId === "consulting-profile") {
    initialData = {
      template_name: "consulting-profile",
      isTemplate: true,
      full_name: "RYAN MONROE",
      email: "ryanmonroe47@gmail.com",
      phone: "(302) 559-2291",
      location: "Wilmington, DE",
      job_title: "Senior Consultant",
      professional_summary:
        "Results-focused business leader with 3+ years' experience bridging the gap between high-impact team management and strategic planning to drive streamlined business performance and position companies for growth. Skilled in defining and delivering against priorities, being an influential relationship builder, and enhancing efficiencies and innovation.",
      work_experience: [
        {
          position: "For a health insurance client",
          company: "Senior Consultant",
          location: "New York, USA",
          start_date: "Jan 2020",
          end_date: "Present",
          description_points: [
            "Led a team of 20+ members supporting performance and compensation review cycles across 51 countries and 11 languages for a Fortune 100 client in sports apparel.",
            "Applied design thinking to recommend continuous service improvements, incident resolution, and defect management.",
            "Managed localization, global and concurrent employment scenarios in SAP HCM.",
          ],
        },
        {
          position: "For a telecom client",
          company: "Business Analyst",
          location: "San Francisco, USA",
          start_date: "Jun 2017",
          end_date: "Dec 2019",
          description_points: [
            "Designed and developed SAP Sweden payroll module for one-off tax processing.",
            "Enhanced SAP standard reports to support Concurrent Employment requirements in Sweden public sector.",
          ],
        },
      ],
      case_studies: [],
      industry_expertise: [
        "Energy",
        "Supply Chain / Logistics",
        "Software & High Tech",
      ],
      functional_expertise: [
        "Data Analytics & Visualization",
        "Data Science",
        "Big Data Platforms",
        "Programming Languages",
        "Databases",
      ],
      programming_languages: ["Angular", "React"],
      databases: ["SQL Server", "Oracle"],
      data_analytics_tools: ["Tableau / Power BI"],
      data_science_tools: ["Pandas / Scikit / PyTorch"],
      big_data_platforms: ["Spark / Hadoop"],
      key_skills: [
        "Energy",
        "Supply Chain / Logistics",
        "Software & High Tech",
        "Data Analytics & Visualization",
        "Data Science",
        "Big Data Platforms",
        "Programming Languages",
        "Databases",
        "Tableau / Power BI",
        "Pandas / Scikit / PyTorch",
        "Spark / Hadoop",
        "Angular",
        "React",
        "SQL Server",
        "Oracle",
      ],
      education: [
        {
          degree: "B.S. Computer Science",
          institution: "Engineering, University Name",
          location: "",
          start_year: "",
          end_year: "",
          description: "",
        },
      ],
      certifications: [
        { name: "Agile CSM - 2023", provider: "" },
        { name: "AZ900 - 2023", provider: "" },
      ],
    };
  } else {
    initialData = {
      template_name: "professional",
      isTemplate: true,
      full_name: "RYAN MONROE",
      email: "ryanmonroe47@gmail.com",
      phone: "(302) 559-2291",
      location: "Wilmington, DE",
      professional_summary:
        "Results-focused business leader with 3+ years' experience bridging the gap between high-impact team management and strategic planning to drive streamlined business performance and position companies for growth. Skilled in defining and delivering against priorities, being an influential relationship builder, and enhancing efficiencies and innovation.",
      key_skills: [
        "Operations Management",
        "Staff Management Development",
        "Training, Coaching & Mentorship",
        "Strategic Planning & Execution",
        "Performance Optimization",
        "Change Management",
        "Critical & Analytical Thinking",
        "Influencing & Consulting",
        "Problem Solving",
        "Solution Development",
        "KPI/Metric Setting & Tracking",
        "Relationship Management",
        "Detail & Deadline-oriented",
        "Customer Engagement",
        "Collaboration & Teamworking",
      ],
      relevant_experience: [
        {
          position: "Director of Operations",
          company: "The Ultimate Shave",
          location: "Glenn Mills, PA",
          start_date: "10/2021",
          end_date: "Present",
          description_points: [
            "Define and direct business operations for 10-person team, scaling sales to $1M+",
            "Lead team development and enhance company culture through motivational leadership",
            "Support onboarding and foster an inclusive culture for all team members",
            "Leverage data and analytics to identify improvement opportunities and drive results",
          ],
        },
        {
          position: "Treasurer",
          company: "Lambda Zeta Chapter of Pi Kappa Alpha",
          location: "Philadelphia, PA",
          start_date: "06/2022",
          end_date: "03/2023",
          description_points: [
            "Generate financial reports and consult stakeholders to improve collections rate from 52% to 87%",
            "Build housing fund and prepare tax forms for organizational compliance",
            "Manage financial operations and ensure fiscal responsibility",
          ],
        },
        {
          position: "Student Consultant",
          company: "Drexel University LeBow College of Business",
          location: "Philadelphia, PA",
          start_date: "01/2023",
          end_date: "03/2023",
          description_points: [
            "Consult companies on business pain points and deliver strategic recommendations",
            "Partner with ice cream company for customer acquisition strategies",
            "Advise recycling company on sustainable improvements and operational efficiency",
            "Deliver recommendations for grant funding and business development",
          ],
        },
      ],
      additional_experience: [
        {
          position: "Barber",
          company: "The Ultimate Shave",
          location: "Glenn Mills, PA",
          start_date: "08/2019",
          end_date: "10/2021",
        },
        {
          position: "Barber Apprentice",
          company: "The Ultimate Shave",
          location: "Glenn Mills, PA",
          start_date: "09/2016",
          end_date: "08/2019",
        },
      ],
      education: [
        {
          degree: "M.B.A. in Finance & Project Management",
          institution: "Drexel University",
          location: "Philadelphia",
          graduation_date: "Expected: 8/2024",
        },
        {
          degree:
            "Bachelor of Arts (B.A.) in Entrepreneurship & Innovation (GPA 3.7)",
          institution: "Drexel University",
          location: "Philadelphia",
          graduation_date: "03/2023",
          minor: "Business Consulting",
          key_coursework:
            "Thought Leadership, Professional Personal Selling, Leadership: Theory & Practice",
          awards_honors:
            "Dean's List, Pi Kappa Alpha/H. Paul Pryor Scholarship, Phi Theta Kappa Scholarship, A,J, Drexel Scholarship",
          extracurricular:
            "Pi Kappa Alpha (ПКА), Close Club (Entrepreneurship)",
        },
      ],
      certifications: [
        "Essentials of Team Collaboration",
        "Consulting Foundations (NASBA)",
      ],
    };
  }
  return cloneResumeData(initialData);
};
const buildFallbackAtsSections = (parsed = {}) => {
  const summaryIntro =
    normalizeText(parsed.professional_summary) ||
    normalizeText(parsed.resume_summary) ||
    normalizeText(
      parsed.relevant_experience?.[0]?.responsibilities?.[0] ||
        parsed.relevant_experience?.[0]?.summary ||
        ""
    );

  const achievements = normalizeArray(parsed.achievements);

  const flattenedSkills = [];
  if (parsed.skills && typeof parsed.skills === "object") {
    Object.values(parsed.skills).forEach((value) => {
      flattenedSkills.push(...normalizeArray(value));
    });
  }

  const lookingToItems = achievements.length
    ? achievements.slice(0, 5)
    : flattenedSkills.slice(0, 5);

  const experienceList = Array.isArray(parsed.relevant_experience)
    ? parsed.relevant_experience
    : Array.isArray(parsed.experience)
    ? parsed.experience
    : [];

  const experienceBullets = experienceList
    .flatMap((exp) =>
      normalizeArray(
        exp?.responsibilities ||
          exp?.description_points ||
          exp?.summary ||
          ""
      )
    )
    .filter(Boolean);

  const expertiseParagraph = experienceBullets.slice(0, 4).join(" ");

  const collaborationParagraph =
    "Open to collaborate on consulting engagements, corporate trainings, and high-impact initiatives.";

  const phone = normalizeText(parsed.personal_info?.phone);
  const email = normalizeText(parsed.personal_info?.email);

  const callToActionPhone = phone
    ? `📞 Let's connect at ${phone} to explore opportunities.`
    : "";
  const callToActionEmail = email
    ? `📧 Reach out at ${email} for collaboration.`
    : "";

  const linkedinValue = normalizeText(parsed.personal_info?.linkedin);
  const whatsappValue = phone
    ? phone.toLowerCase().includes("whatsapp")
      ? phone
      : `WhatsApp: ${phone}`
    : "";

  const highlightSections = [];
  if (flattenedSkills.length) {
    highlightSections.push({
      title: "Core Skills & Technologies",
      items: flattenedSkills.slice(0, 10),
    });
  }
  if (achievements.length) {
    highlightSections.push({
      title: "Key Achievements",
      items: achievements.slice(0, 8),
    });
  }
  if (experienceBullets.length) {
    highlightSections.push({
      title: "Experience Highlights",
      items: experienceBullets.slice(0, 8),
    });
  }
  if (!highlightSections.length && lookingToItems.length) {
    highlightSections.push({
      title: "Highlights",
      items: lookingToItems.slice(0, 8),
    });
  }

  return {
    summary_intro: summaryIntro,
    looking_to_items: lookingToItems,
    expertise_paragraph: expertiseParagraph,
    collaboration_paragraph: collaborationParagraph,
    call_to_action_phone: callToActionPhone,
    call_to_action_email: callToActionEmail,
    linkedin: linkedinValue,
    whatsapp: whatsappValue,
    highlight_sections: highlightSections,
  };
};

// Using react-to-print for PDF generation
const ResumeEditor = ({
  templateId,
  onBack,
  resumeData: externalResumeData,
  autoDownload = false,
  onAutoDownloadComplete,
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [showCoverLetter, setShowCoverLetter] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [activeField, setActiveField] = useState(null); // Track which field is being edited
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimeoutRef = useRef(null);
  const autoDownloadInFlightRef = useRef(false);
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);
  const showSaveFeedback = (message, timeout = 4000) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(message);
    setShowToast(true);
    if (timeout > 0) {
      toastTimeoutRef.current = setTimeout(() => {
        setShowToast(false);
        setTimeout(() => setToastMessage(""), 300);
      }, timeout);
    }
  };
  // Create Resume Modal states
  const [showCreateResumeModal, setShowCreateResumeModal] = useState(false);
  // Handle uploading resume from modal - SIMPLIFIED with new backend structure
  const handleUploadResumeFromModal = (parsedData) => {
    setShowCreateResumeModal(false); // Close the modal
    if (parsedData) {
      const parsed = parsedData;
      const atsSections = parsed.ats_sections || {};
      const fallbackAts = buildFallbackAtsSections(parsed);
      const sanitizedHighlightSections = Array.isArray(
        atsSections.highlight_sections
      )
        ? atsSections.highlight_sections
            .map((section) => ({
              title: normalizeText(section?.title),
              items: normalizeArray(section?.items),
            }))
            .filter((section) => section.items.length > 0)
        : [];
      const resolvedSummaryIntro =
        normalizeText(atsSections.summary_intro) ||
        fallbackAts.summary_intro;
      const resolvedLookingToItems =
        normalizeArray(atsSections.looking_to_items).length > 0
          ? normalizeArray(atsSections.looking_to_items)
          : fallbackAts.looking_to_items;
      const resolvedExpertiseParagraph =
        normalizeText(atsSections.expertise_paragraph) ||
        fallbackAts.expertise_paragraph;
      const resolvedCollaborationParagraph =
        normalizeText(atsSections.collaboration_paragraph) ||
        fallbackAts.collaboration_paragraph;
      const resolvedPhoneCTA =
        normalizeText(atsSections.call_to_action_phone) ||
        fallbackAts.call_to_action_phone;
      const resolvedEmailCTA =
        normalizeText(atsSections.call_to_action_email) ||
        fallbackAts.call_to_action_email;
      const resolvedLinkedIn =
        normalizeText(atsSections.linkedin) || fallbackAts.linkedin;
      const resolvedWhatsapp =
        normalizeText(atsSections.whatsapp) || fallbackAts.whatsapp;
      const resolvedHighlightSections =
        sanitizedHighlightSections.length > 0
          ? sanitizedHighlightSections
          : fallbackAts.highlight_sections;
      // Backend now returns clean, consistent data structure - minimal mapping needed!
      let finalMappedData = {
        template_name: templateId || "professional",
        isTemplate: false,
        isUploadedResume: true,
        // Personal Info - use null for missing optional fields so templates don't show defaults
        full_name: parsed.personal_info?.full_name || "",
        job_title: parsed.personal_info?.job_title || null,
        email: parsed.personal_info?.email || "",
        phone: parsed.personal_info?.phone || "",
        location: parsed.personal_info?.location || null,
        address: parsed.personal_info?.location || null,
        github: fixSocialMediaUrl(parsed.personal_info?.github) || null,
        linkedin:
          fixSocialMediaUrl(
            parsed.personal_info?.linkedin || resolvedLinkedIn || ""
          ) || null,
        leetcode: fixSocialMediaUrl(parsed.personal_info?.leetcode) || null,
        hackerrank: fixSocialMediaUrl(parsed.personal_info?.hackerrank) || null,
        portfolio: fixSocialMediaUrl(parsed.personal_info?.portfolio) || null,
        whatsapp: resolvedWhatsapp || null,
        // Professional Summary - null if not present
        professional_summary: parsed.professional_summary || null,
        summary_intro: resolvedSummaryIntro || parsed.professional_summary || null,
        looking_to_items: resolvedLookingToItems,
        expertise_paragraph: resolvedExpertiseParagraph,
        collaboration_paragraph: resolvedCollaborationParagraph,
        call_to_action_phone: resolvedPhoneCTA,
        call_to_action_email: resolvedEmailCTA,
        // Skills - backend already provides structured format
        skills: parsed.skills || {
          programming_languages: [],
          tools: [],
          frameworks: [],
          ai_ml_technologies: [],
        },
        key_skills: parsed.skills?.programming_languages || [],
        technical_skills: parsed.skills?.tools || [],
        // Experience - NEW backend returns relevant_experience (base44 style)
        relevant_experience: (
          parsed.relevant_experience ||
          parsed.work_experience ||
          []
        ).map((exp) => ({
          position: exp.position || "",
          company: exp.company || "",
          location: exp.location || "",
          start_date: exp.start_date || "",
          end_date: exp.end_date || "",
          description_points: exp.responsibilities || [],
        })),
        // Education - NEW backend returns exact dates as start_date/end_date
        education: (parsed.education || []).map((edu) => {
          const mappedEdu = {
            degree: edu.degree || "",
            institution: edu.institution || "",
            institute: edu.institution || "",
            field_of_study: edu.field_of_study || "",
            location: edu.location || "",
            start_year: edu.start_date || "",
            end_year: edu.end_date || "",
            start_date: edu.start_date || "",
            end_date: edu.end_date || "",
            cgpa: edu.gpa || "", // Maps backend 'gpa' to frontend 'cgpa'
            gpa: edu.gpa || "", // Also keep 'gpa' field
            coursework: edu.coursework || [],
          };
          return mappedEdu;
        }),
        // Projects - NEW backend extracts exact data with year field
        projects: (parsed.projects || []).map((p) => {
          const mappedProj = {
            title: p.name || "",
            name: p.name || "",
            description: Array.isArray(p.description)
              ? p.description
              : p.description
                ? [p.description]
                : [],
            technologies: p.technologies || [],
            tech_stack: p.technologies || [],
            github_link: p.github_url || "",
            link: p.github_url || "",
            year: p.year || "",
          };
          return mappedProj;
        }),
        // Certifications and Achievements - direct mapping
        certifications: parsed.certifications || [],
        achievements: parsed.achievements || [],
        // ✅ NEW FIELDS - Languages, Interests, Publications, etc.
        // ✅ Transform languages to STRINGS (CRITICAL for ALL templates!)
        languages: (parsed.languages || []).map((lang) => {
          if (typeof lang === "string") return lang;
          if (typeof lang === "object" && lang !== null) {
            const languageName = lang.language || lang.name || "";
            const proficiencyLevel = lang.proficiency || "";
            if (languageName && proficiencyLevel) {
              return `${languageName} (${proficiencyLevel})`;
            }
            return languageName || "";
          }
          return String(lang);
        }),
        interests: parsed.interests || [],
        publications: parsed.publications || [],
        volunteer_work: parsed.volunteer_work || [],
        additional_info: parsed.additional_info || [],
        // Additional fields
        websites: [],
        resume_summary: parsed.resume_summary || "",
        cover_letter: parsed.cover_letter || "",
        key_bullet_sections: resolvedHighlightSections,
        ats_sections: {
          summary_intro: resolvedSummaryIntro,
          looking_to_items: resolvedLookingToItems,
          expertise_paragraph: resolvedExpertiseParagraph,
          collaboration_paragraph: resolvedCollaborationParagraph,
          call_to_action_phone: resolvedPhoneCTA,
          call_to_action_email: resolvedEmailCTA,
          linkedin: resolvedLinkedIn,
          whatsapp: resolvedWhatsapp,
          highlight_sections: resolvedHighlightSections,
        },
      };
      console.log("[ATS Upload] Final mapped data preview:", {
        summary_intro: finalMappedData.summary_intro,
        looking_to_items: finalMappedData.looking_to_items,
        expertise_paragraph: finalMappedData.expertise_paragraph,
        collaboration_paragraph: finalMappedData.collaboration_paragraph,
        call_to_action_phone: finalMappedData.call_to_action_phone,
        call_to_action_email: finalMappedData.call_to_action_email,
        key_bullet_sections: finalMappedData.key_bullet_sections,
      });
      if (!finalMappedData.key_bullet_sections.length) {
        const fallbackHighlights = [
          ...(finalMappedData.skills?.programming_languages || []),
          ...(finalMappedData.skills?.frameworks || []),
          ...(finalMappedData.skills?.tools || []),
          ...(finalMappedData.skills?.ai_ml_technologies || []),
        ]
          .map((item) => normalizeText(item))
          .filter((item) => item.length > 0)
          .slice(0, 8);
        if (fallbackHighlights.length) {
          finalMappedData.key_bullet_sections = [
            {
              title: "Key Highlights",
              items: fallbackHighlights,
            },
          ];
        }
      }
      // ✅ Projects already mapped above - backend now handles GitHub link matching
      // ✅ Achievements already mapped above
      // ✅ No need for complex GitHub link matching - backend does it!
      // Apply template-specific transformations (same as lines 691-1100 in useEffect)
      // IMPORTANT: Experience mapping happens INSIDE each template block, not here!
      if (templateId === "simple") {
        finalMappedData.highlights = finalMappedData.key_skills || [];
        finalMappedData.address = finalMappedData.location || "";
        finalMappedData.work_experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
          position: exp.position || "",
          company: exp.company || "",
          location: exp.location || "",
          start_date: exp.start_date || "",
          end_date: exp.end_date || "",
          duties: extractExperienceBullets(exp),
        }));
        finalMappedData.skills = finalMappedData.skills;
        finalMappedData.projects = finalMappedData.projects;
        finalMappedData.achievements = finalMappedData.achievements;
        finalMappedData.certifications = finalMappedData.certifications;
        delete finalMappedData.location; // Only delete location for simple template
      }
      if (templateId === "professional") {
        finalMappedData.address = finalMappedData.location || "";
        // DON'T delete location for professional template
        finalMappedData.structured_skills = finalMappedData.skills;
        finalMappedData.key_skills = [
          ...(finalMappedData.skills.programming_languages || []),
          ...(finalMappedData.skills.frameworks || []),
          ...(finalMappedData.skills.tools || []),
          ...(finalMappedData.skills.ai_ml_technologies || []),
        ];
      } else if (templateId === "modern") {
        finalMappedData.address = finalMappedData.location || "";
        // DON'T delete location for modern template
        finalMappedData.structured_skills = finalMappedData.skills;
        finalMappedData.professional_skills = [
          ...(finalMappedData.skills.programming_languages || []),
          ...(finalMappedData.skills.frameworks || []),
        ];
        finalMappedData.technical_skills = [
          ...(finalMappedData.skills.tools || []),
          ...(finalMappedData.skills.ai_ml_technologies || []),
        ];
        // Education was already correctly mapped at lines 94-104 with start_year and end_year
        // Don't remap it - just keep it as is (it already has institution field from line 97)
        // Map experience data for modern template
        finalMappedData.experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
          position: exp.position || "",
          company: exp.company || "",
          location: exp.location || "",
          start_date: exp.start_date || "",
          end_date: exp.end_date || "",
          description_points: extractExperienceBullets(exp),
        }));
        // IMPORTANT: Don't auto-generate job_title for uploaded resumes
        // Let user edit it themselves if it's empty or not in their resume
        // The main useEffect auto-generates and falls back to "Professional" which is wrong for uploaded resumes
      } else if (templateId === "security-manager") {
        finalMappedData.address = finalMappedData.location || "";
        // DON'T delete location for security-manager template
        finalMappedData.technical_competencies = [
          ...(finalMappedData.skills.programming_languages || []),
          ...(finalMappedData.skills.frameworks || []),
          ...(finalMappedData.skills.tools || []),
          ...(finalMappedData.skills.ai_ml_technologies || []),
        ];
        // Map work_experience data for security-manager template
        finalMappedData.work_experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
          position: exp.position || "",
          company: exp.company || "",
          location: exp.location || "",
          start_date: exp.start_date || "",
          end_date: exp.end_date || "",
          responsibilities: extractExperienceBullets(exp),
        }));
      } else if (
        templateId === "computer-teacher" ||
        templateId === "tech-resume" ||
        templateId === "pe-teacher" ||
        templateId === "science-teacher" ||
        templateId === "english-teacher"
      ) {
        finalMappedData.address = finalMappedData.location || "";
        // Map work_experience data for teacher templates
        finalMappedData.work_experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
          position: exp.position || "",
          company: exp.company || "",
          location: exp.location || "",
          start_date: exp.start_date || "",
          end_date: exp.end_date || "",
          responsibilities: extractExperienceBullets(exp),
        }));
        // ✅ DON'T delete relevant_experience - form needs it for prefilling!
        // Keep both work_experience (for preview) and relevant_experience (for form)
        // Delete location for english-teacher, pe-teacher, science-teacher (as per line 1468-1470 in main useEffect)
        if (
          ["pe-teacher", "science-teacher", "english-teacher"].includes(
            templateId
          )
        ) {
          delete finalMappedData.location;
        }
      } else if (templateId === "marketing-specialist") {
        finalMappedData.address = finalMappedData.location || "";
        // DON'T delete location for marketing-specialist template
      } else if (templateId === "business-development") {
        finalMappedData.address = finalMappedData.location || "";
        // DON'T delete location for business-development template
        if (
          finalMappedData.skills &&
          typeof finalMappedData.skills === "object"
        ) {
          finalMappedData.structured_skills = finalMappedData.skills;
          const allSkills = [];
          if (finalMappedData.skills.programming_languages)
            allSkills.push(...finalMappedData.skills.programming_languages);
          if (finalMappedData.skills.frameworks)
            allSkills.push(...finalMappedData.skills.frameworks);
          if (finalMappedData.skills.tools)
            allSkills.push(...finalMappedData.skills.tools);
          if (finalMappedData.skills.ai_ml_technologies)
            allSkills.push(...finalMappedData.skills.ai_ml_technologies);
          if (finalMappedData.key_skills)
            allSkills.push(...finalMappedData.key_skills);
          finalMappedData.core_competencies = allSkills.filter(Boolean);
          finalMappedData.tools_tech = finalMappedData.skills.tools || [];
        } else {
          finalMappedData.core_competencies = finalMappedData.key_skills || [];
          finalMappedData.tools_tech = finalMappedData.technical_skills || [];
        }
      } else if (templateId === "cv-guy") {
        finalMappedData.address = finalMappedData.location || "";
        // DON'T delete location for cv-guy template
      }
      // DON'T delete professional_summary - all templates can use it if available
      if (
        templateId === "simple" ||
        templateId === "security-manager" ||
        templateId === "cv-guy"
      ) {
        if (
          finalMappedData.skills &&
          typeof finalMappedData.skills === "object"
        ) {
          finalMappedData.key_skills = [
            ...(finalMappedData.skills.programming_languages || []),
            ...(finalMappedData.skills.frameworks || []),
            ...(finalMappedData.skills.tools || []),
            ...(finalMappedData.skills.ai_ml_technologies || []),
          ];
          finalMappedData.structured_skills = finalMappedData.skills;
        } else {
          finalMappedData.skills = finalMappedData.key_skills || [];
          delete finalMappedData.key_skills;
        }
      }
      // NOTE: relevant_experience is kept for professional and marketing-specialist templates
      // Other templates map it to their specific fields (experience/work_experience) and delete it
      // Set the processed data
      setResumeData(finalMappedData);
      calculateCompletion(finalMappedData);
    }
  };
  // Handle creating new resume from scratch
  const handleCreateNewResume = () => {
    setShowCreateResumeModal(false);
    // Reset to template data (start fresh)
    const templateData = getTemplateData(templateId, null);
    setResumeData(templateData);
    calculateCompletion(templateData);
  };
  const handleSaveCurrentTemplate = () => {
    if (!resumeData) {
      showSaveFeedback("Nothing to save yet.", 3000);
      return;
    }
    try {
      const snapshot = cloneResumeData(resumeData);
      const normalizedTemplateId =
        templateId || snapshot.template_name || "professional";
      snapshot.template_name = normalizedTemplateId;
      snapshot.__savedTemplate = true;
      const existingId =
        snapshot.__savedId ||
        `cv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      snapshot.__savedId = existingId;
      const entry = {
        id: existingId,
        templateId: normalizedTemplateId,
        templateLabel: formatTemplateLabel(normalizedTemplateId),
        savedAt: new Date().toISOString(),
        name: snapshot.full_name || "Untitled Resume",
        data: snapshot,
      };
      let storedTemplates = [];
      const raw = localStorage.getItem(SAVED_TEMPLATES_STORAGE_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            storedTemplates = parsed.filter((item) => item?.id !== existingId);
          }
        } catch (error) {
          console.error("Failed to parse saved templates", error);
        }
      }
      storedTemplates.unshift(entry);
      localStorage.setItem(
        SAVED_TEMPLATES_STORAGE_KEY,
        JSON.stringify(storedTemplates)
      );
      setResumeData((prev) => {
        if (!prev) return snapshot;
        return {
          ...prev,
          __savedId: existingId,
          __savedTemplate: true,
          template_name: normalizedTemplateId,
        };
      });
      showSaveFeedback("Template saved to CV Templates.");
    } catch (error) {
      console.error("Failed to save template", error);
      showSaveFeedback("Unable to save template. Please try again.", 5000);
    }
  };
  // ✅ Helper function to fix social media URLs
  const fixSocialMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    // Remove leading/trailing whitespace
    url = url.trim();
    // If URL is empty after trim, return empty
    if (!url) return '';
    // If it already starts with http:// or https://, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If it starts with www., add https://
    if (url.startsWith('www.')) {
      return `https://${url}`;
    }
    // For URLs like "linkedin.com/in/..." or "github.com/...", add https://
    if (url.includes('.com/') || url.includes('.io/') || url.includes('.org/')) {
      return `https://${url}`;
    }
    // For partial paths like "in/username", try to detect the platform and add full URL
    if (url.startsWith('in/') || url.includes('/in/')) {
      return `https://linkedin.com/${url.startsWith('in/') ? url : url.split('/in/')[1] ? 'in/' + url.split('/in/')[1] : url}`;
    }
    // Default: add https:// prefix
    return `https://${url}`;
  };
  // Helper functions for GitHub link matching (accessible throughout component)
  const extractProjectNameFromUrl = (url) => {
    if (!url) return "";
    const parts = url.split("/");
    return parts[parts.length - 1] || "";
  };
  const normalizeTitle = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "") // Remove special characters
      .replace(/\s+/g, ""); // Remove spaces
  };
  const getAvailableGithubLinks = (projects) => {
    const availableGithubLinks = [];
    if (projects) {
      projects.forEach((p) => {
        if (p.github_link || p.link) {
          const url = p.github_link || p.link;
          const projectName = extractProjectNameFromUrl(url);
          if (projectName) {
            availableGithubLinks.push({
              url: url,
              projectName: projectName,
              normalizedName: normalizeTitle(projectName),
            });
          }
        }
      });
    }
    return availableGithubLinks;
  };
  // Enhanced matching function with multiple strategies
  const findMatchingGithubLink = (
    projectTitle,
    availableGithubLinks,
    projectData
  ) => {
    const normalizedProjectTitle = normalizeTitle(projectTitle);
    // EASY STEP: If project has its own GitHub link, validate it matches the project title
    if (projectData.github_link || projectData.link) {
      const projectUrl = projectData.github_link || projectData.link;
      const projectNameFromUrl = extractProjectNameFromUrl(projectUrl);
      const normalizedProjectNameFromUrl = normalizeTitle(projectNameFromUrl);
      // Only use the project's own GitHub link if it matches the project title
      if (
        normalizedProjectNameFromUrl === normalizedProjectTitle ||
        normalizedProjectTitle.includes(normalizedProjectNameFromUrl) ||
        normalizedProjectNameFromUrl.includes(normalizedProjectTitle)
      ) {
        return projectUrl;
      }
    }
    // Strategy 1: Exact title matching (existing logic - PRESERVED)
    let matchedLink = availableGithubLinks.find(
      (link) =>
        link.normalizedName === normalizedProjectTitle ||
        normalizedProjectTitle.includes(link.normalizedName) ||
        link.normalizedName.includes(normalizedProjectTitle)
    );
    if (matchedLink) {
      return matchedLink.url;
    }
    // Strategy 2: Keyword-based matching
    const projectKeywords = projectTitle
      .toLowerCase()
      .split(/[\s\-_]+/)
      .filter((word) => word.length > 2);
    matchedLink = availableGithubLinks.find((link) => {
      const linkKeywords = link.projectName
        .toLowerCase()
        .split(/[\s\-_]+/)
        .filter((word) => word.length > 2);
      // Check if at least 2 keywords match
      const matchingKeywords = projectKeywords.filter((keyword) =>
        linkKeywords.some(
          (linkKeyword) =>
            keyword.includes(linkKeyword) || linkKeyword.includes(keyword)
        )
      );
      return matchingKeywords.length >= Math.min(2, projectKeywords.length);
    });
    if (matchedLink) {
      return matchedLink.url;
    }
    // Strategy 3: Description-based matching
    if (projectData.description) {
      const description = Array.isArray(projectData.description)
        ? projectData.description.join(" ").toLowerCase()
        : projectData.description.toLowerCase();
      matchedLink = availableGithubLinks.find((link) => {
        const linkKeywords = link.projectName
          .toLowerCase()
          .split(/[\s\-_]+/)
          .filter((word) => word.length > 2);
        return linkKeywords.some((keyword) => description.includes(keyword));
      });
      if (matchedLink) {
        return matchedLink.url;
      }
    }
    // Strategy 4: Tech stack-based matching
    if (projectData.tech_stack || projectData.technologies) {
      const techStack = Array.isArray(
        projectData.tech_stack || projectData.technologies
      )
        ? (projectData.tech_stack || projectData.technologies)
            .join(" ")
            .toLowerCase()
        : (
            projectData.tech_stack ||
            projectData.technologies ||
            ""
          ).toLowerCase();
      matchedLink = availableGithubLinks.find((link) => {
        const linkKeywords = link.projectName
          .toLowerCase()
          .split(/[\s\-_]+/)
          .filter((word) => word.length > 2);
        return linkKeywords.some((keyword) => techStack.includes(keyword));
      });
      if (matchedLink) {
        return matchedLink.url;
      }
    }
    // Strategy 5: Fuzzy matching with similarity score
    matchedLink = availableGithubLinks.find((link) => {
      const similarity = calculateSimilarity(
        normalizedProjectTitle,
        link.normalizedName
      );
      return similarity > 0.6; // 60% similarity threshold
    });
    if (matchedLink) {
      return matchedLink.url;
    }
    return ""; // No match found
  };
  // Helper function to calculate string similarity
  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    if (longer.length === 0) return 1.0;
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };
  // Helper function to calculate Levenshtein distance
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };
  // Image upload handler for preview
  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", file);
      // Simple fetch request to upload file
      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        setResumeData((prev) => ({
          ...prev,
          profile_image_url: result.file_url || URL.createObjectURL(file),
        }));
      } else {
        // Fallback to local preview if upload fails
        setResumeData((prev) => ({
          ...prev,
          profile_image_url: URL.createObjectURL(file),
        }));
      }
    } catch (error) {
      // Fallback to local preview
      setResumeData((prev) => ({
        ...prev,
        profile_image_url: URL.createObjectURL(file),
      }));
    }
  };
  // Function to clean HTML tags from text
  const cleanHtmlTags = (text) => {
    if (typeof text !== "string") return text;
    return text.replace(/<[^>]*>/g, "");
  };
  // Tailored Resume states
  const [showTailoredResume, setShowTailoredResume] = useState(false);
  const [jdFile, setJdFile] = useState(null);
  const [jdText, setJdText] = useState(""); // New: JD text input
  const [jdInputType, setJdInputType] = useState("file"); // 'file' or 'text'
  const [isGeneratingTailored, setIsGeneratingTailored] = useState(false);
  const [tailoredResumeData, setTailoredResumeData] = useState(null);
  const [tailoredError, setTailoredError] = useState("");
  const [showJDUpload, setShowJDUpload] = useState(false);
  const summaryRef = useRef();
  const componentRef = useRef();
  // Print functionality - exactly like Resume-Builder
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        html, body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
    documentTitle: resumeData?.full_name 
      ? `${resumeData.full_name.replace(/\s+/g, '_')}_Resume` 
      : 'Resume',
    removeAfterPrint: false,
    copyStyles: true,
    onBeforePrint: () => {
      setLoading(true);
    },
    onAfterPrint: () => {
      setLoading(false);
      if (autoDownloadInFlightRef.current) {
        autoDownloadInFlightRef.current = false;
        if (onAutoDownloadComplete) {
          onAutoDownloadComplete();
        }
      }
    },
  });
  useEffect(() => {
    if (autoDownload && resumeData && componentRef.current) {
      if (!autoDownloadInFlightRef.current) {
        autoDownloadInFlightRef.current = true;
        requestAnimationFrame(() => {
          handlePrint();
        });
      }
    } else if (!autoDownload && autoDownloadInFlightRef.current === true) {
      autoDownloadInFlightRef.current = false;
    }
  }, [autoDownload, resumeData, handlePrint]);
  useEffect(() => {
    if (templateId === "professional" && externalResumeData?.resume_summary) {
      setTimeout(() => {
        summaryRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, [externalResumeData, templateId]);
  // Enhanced template data mapping with better validation
  const getTemplateData = (templateId, data) => {
    templateId = templateId || data?.template_name || "professional";
    let effectiveData = data;
    const hasExternalData = !!effectiveData;
    if (!hasExternalData) {
      effectiveData = getLegacyTemplateData(templateId) || {};
    }
    const baseData = {
      template_name: templateId,
      // ✅ Preserve flags for dynamic sections
      // If data exists, it's an upload (isTemplate=false, isUploadedResume=true)
      // If no data, it's a template (isTemplate=true, isUploadedResume=false)
      isTemplate: hasExternalData ? false : true,
      isUploadedResume: hasExternalData,
      // ✅ Add top-level fields needed by templates
      full_name: effectiveData?.full_name || effectiveData?.name || "",
      job_title: effectiveData?.job_title || effectiveData?.title || "",
      email: effectiveData?.email || "",
      phone: effectiveData?.phone || effectiveData?.contact_number || "",
      location: effectiveData?.location || effectiveData?.address || "",
      professional_summary:
        effectiveData?.professional_summary || effectiveData?.summary || "",
      summary_intro: effectiveData?.summary_intro || "",
      looking_to_items: Array.isArray(effectiveData?.looking_to_items)
        ? effectiveData.looking_to_items
        : [],
      expertise_paragraph: effectiveData?.expertise_paragraph || "",
      collaboration_paragraph: effectiveData?.collaboration_paragraph || "",
      call_to_action_phone: effectiveData?.call_to_action_phone || "",
      call_to_action_email: effectiveData?.call_to_action_email || "",
      personalInfo: {
        name: effectiveData?.full_name || effectiveData?.name || "",
        email: effectiveData?.email || "",
        phone: effectiveData?.phone || effectiveData?.contact_number || "",
        address: effectiveData?.address || effectiveData?.location || "",
        linkedin: fixSocialMediaUrl(effectiveData?.linkedin) || "",
        github: fixSocialMediaUrl(effectiveData?.github) || "",
        portfolio: fixSocialMediaUrl(
          effectiveData?.portfolio ||
            effectiveData?.website ||
            (Array.isArray(effectiveData?.websites)
              ? effectiveData.websites[0]
              : "")
        ),
      },
      linkedin: fixSocialMediaUrl(effectiveData?.linkedin) || "",
      whatsapp: effectiveData?.whatsapp || "",
      summary: effectiveData?.professional_summary || effectiveData?.summary || "",
      experience: effectiveData?.relevant_experience ||
        effectiveData?.experience ||
        effectiveData?.work_experience ||
        [],
      relevant_experience: effectiveData?.relevant_experience || [],
      education: effectiveData?.education || [],
      skills:
        effectiveData?.skills || effectiveData?.key_skills || effectiveData?.highlights || [],
      projects: effectiveData?.projects || [],
      certifications: effectiveData?.certifications || [],
      // ✅ ALWAYS transform languages to STRINGS for ALL templates (no exceptions!)
      languages: (effectiveData?.languages || []).map((lang) => {
        if (typeof lang === "string") return lang;
        if (typeof lang === "object" && lang !== null) {
          const languageName = lang.language || lang.name || "";
          const proficiencyLevel = lang.proficiency || "";
          if (languageName && proficiencyLevel) {
            return `${languageName} (${proficiencyLevel})`;
          }
          return languageName || "";
        }
        return String(lang);
      }),
      interests: effectiveData?.interests || [],
      publications: effectiveData?.publications || [],
      volunteer_work: effectiveData?.volunteer_work || [],
      additional_info: effectiveData?.additional_info || [],
      achievements: effectiveData?.achievements || [],
      key_bullet_sections: effectiveData?.key_bullet_sections || [],
    };
    // Template-specific data mapping
    switch (templateId) {
      case "simple":
        const simpleMappedData = {
          ...baseData,
          isTemplate: baseData.isTemplate !== false, // Preserve isTemplate flag
          highlights: baseData.skills,
          work_experience: baseData.experience.map((exp) => ({
            position: exp.position || "",
            company: exp.company || "",
            location: exp.location || "",
            start_date: exp.start_date || exp.start_year || "",
            end_date: exp.end_date || exp.end_year || "",
            duties: extractExperienceBullets(exp),
          })),
        };
        // ✅ Only set additional_sections and defaults for TEMPLATE data
        if (baseData.isTemplate) {
          simpleMappedData.additional_sections = {
            certifications: ["List of relevant certifications"],
            awards_honors: ["Remembrance/Honor received"],
            volunteer_experience: ["Member of department/organization"],
            professional_affiliations: [],
          };
          simpleMappedData.references_note = "References Available on request.";
        }
        // ✅ For uploaded resumes: Don't create additional_sections at all
        // Certifications, volunteer_work, publications will be DYNAMIC SECTIONS
        return simpleMappedData;
      case "professional":
        return {
          ...baseData,
          structured_skills: baseData.skills,
          key_skills: Array.isArray(baseData.skills)
            ? baseData.skills
            : Object.values(baseData.skills || {})
                .flat()
                .filter(Boolean),
        };
      case "modern":
        return {
          ...baseData,
          structured_skills: baseData.skills,
          professional_skills: Array.isArray(baseData.skills)
            ? baseData.skills
            : Object.values(baseData.skills || {})
                .flat()
                .filter(Boolean),
          technical_skills: Array.isArray(baseData.skills)
            ? baseData.skills
            : Object.values(baseData.skills || {})
                .flat()
                .filter(Boolean),
        };
      case "tech-resume":
        return {
          ...baseData,
          structured_skills: baseData.skills,
          key_skills: Array.isArray(baseData.skills)
            ? baseData.skills
            : Object.values(baseData.skills || {})
                .flat()
                .filter(Boolean),
        };
      case "consulting-profile":
        // Helper function to categorize skills from uploaded resume
        const pushTokens = (target, tokens) => {
          tokens
            .map((item) => normalizeText(item))
            .filter(Boolean)
            .forEach((item) => target.push(item));
        };

        const categorizeSkills = (skills) => {
          const categorized = {
            industry: [],
            functional: [],
            programming: [],
            databases: [],
            analytics: [],
            dataScience: [],
            bigData: [],
          };

          if (!skills) {
            return categorized;
          }

          const sectionLabelKeywords = [
            'industry expertise',
            'functional expertise',
            'programming languages',
            'databases',
            'database',
            'data analytics tools',
            'analytics tools',
            'data science tools',
            'big data platforms',
          ];

          const industryKeywords = ['industry', 'energy', 'supply chain', 'logistics', 'software', 'tech', 'healthcare', 'finance', 'banking', 'retail', 'manufacturing', 'consulting'];
          const functionalKeywords = ['functional', 'data analytics', 'data science', 'big data', 'business intelligence', 'machine learning', 'artificial intelligence', 'analytics', 'visualization', 'project management', 'strategy', 'leadership'];
          const programmingKeywords = ['programming', 'python', 'java', 'javascript', 'typescript', 'c++', 'c#', 'angular', 'react', 'vue', 'node', 'php', 'ruby', 'go', 'rust', 'swift', 'kotlin'];
          const databaseKeywords = ['database', 'databases', 'sql', 'mysql', 'postgresql', 'oracle', 'mongodb', 'redis', 'elasticsearch', 'cassandra', 'dynamodb', 'sql server', 'db2'];
          const analyticsKeywords = ['analytics', 'tableau', 'power bi', 'excel', 'spss', 'sas', 'r ', ' r$', 'matlab', 'looker', 'qlik', 'd3.js', 'plotly'];
          const dataScienceKeywords = ['data science', 'pandas', 'numpy', 'scikit', 'tensorflow', 'pytorch', 'keras', 'xgboost', 'lightgbm', 'catboost', 'opencv', 'nltk', 'spacy'];
          const bigDataKeywords = ['big data', 'spark', 'hadoop', 'kafka', 'storm', 'flink', 'airflow', 'kubernetes', 'docker', 'aws', 'azure', 'gcp', 'databricks'];

          const ensureArray = Array.isArray(skills) ? skills : Object.values(skills || {});

          ensureArray.forEach((skillEntry) => {
            const skill =
              typeof skillEntry === 'string'
                ? skillEntry
                : skillEntry?.name || skillEntry?.skill || skillEntry?.title || '';

            const raw = normalizeText(skill);
            if (!raw) return;

            const colonIndex = raw.indexOf(':');
            if (colonIndex > -1) {
              const label = normalizeText(raw.slice(0, colonIndex));
              const rest = raw.slice(colonIndex + 1);
              const tokens = normalizeArray(rest);
              switch (label.toLowerCase()) {
                case 'industry expertise':
                  pushTokens(categorized.industry, tokens);
                  return;
                case 'functional expertise':
                  pushTokens(categorized.functional, tokens);
                  return;
                case 'programming languages':
                  pushTokens(categorized.programming, tokens);
                  return;
                case 'databases':
                case 'database':
                  pushTokens(categorized.databases, tokens);
                  return;
                case 'data analytics tools':
                case 'analytics tools':
                  pushTokens(categorized.analytics, tokens);
                  return;
                case 'data science tools':
                  pushTokens(categorized.dataScience, tokens);
                  return;
                case 'big data platforms':
                  pushTokens(categorized.bigData, tokens);
                  return;
                default:
                  pushTokens(categorized.functional, tokens.length ? tokens : [rest]);
                  return;
              }
            }

            const skillLower = raw.toLowerCase();
            if (sectionLabelKeywords.includes(skillLower)) {
              return;
            }
            let matched = false;

            if (industryKeywords.some((keyword) => skillLower.includes(keyword))) {
              categorized.industry.push(raw);
              matched = true;
            }
            if (!matched && functionalKeywords.some((keyword) => skillLower.includes(keyword))) {
              categorized.functional.push(raw);
              matched = true;
            }
            if (!matched && programmingKeywords.some((keyword) => skillLower.includes(keyword))) {
              categorized.programming.push(raw);
              matched = true;
            }
            if (!matched && databaseKeywords.some((keyword) => skillLower.includes(keyword))) {
              categorized.databases.push(raw);
              matched = true;
            }
            if (!matched && analyticsKeywords.some((keyword) => skillLower.includes(keyword))) {
              categorized.analytics.push(raw);
              matched = true;
            }
            if (!matched && dataScienceKeywords.some((keyword) => skillLower.includes(keyword))) {
              categorized.dataScience.push(raw);
              matched = true;
            }
            if (!matched && bigDataKeywords.some((keyword) => skillLower.includes(keyword))) {
              categorized.bigData.push(raw);
              matched = true;
            }

            if (!matched) {
              categorized.functional.push(raw);
            }
          });

          Object.keys(categorized).forEach((key) => {
        categorized[key] = normalizeSkillList(categorized[key]);
          });
          
          return categorized;
        };
        
        const uploadedSkills = Array.isArray(baseData.skills)
          ? baseData.skills
          : Array.isArray(baseData.key_skills)
            ? baseData.key_skills
            : Object.values(baseData.skills || {})
                .flat()
                .filter(Boolean);
        const categorizedSkills = categorizeSkills(uploadedSkills);
        
        return {
          ...baseData,
          // Preserve relevant_experience for Consulting template
          relevant_experience: baseData.isTemplate ? baseData.relevant_experience : (baseData.relevant_experience || []).map(exp => ({
            position: exp.position || "",
            company: exp.company || "",
            location: exp.location || "",
            start_date: exp.start_date || exp.start_year || "",
            end_date: exp.end_date || exp.end_year || "",
            description_points: extractExperienceBullets(exp)
          })),
          // Map experience data to work_experience for Consulting template
          work_experience: baseData.isTemplate ? [
            {
              position: "For a health insurance client",
              company: "Senior Consultant",
              location: "New York, USA",
              start_date: "Jan 2020",
              end_date: "Present",
              description_points: [
                "Led a team of 20+ members supporting performance and compensation review cycles across 51 countries and 11 languages for a Fortune 100 client in sports apparel.",
                "Applied design thinking to recommend continuous service improvements, incident resolution, and defect management.",
                "Managed localization, global and concurrent employment scenarios in SAP HCM."
              ]
            },
            {
              position: "For a telecom client",
              company: "Business Analyst",
              location: "San Francisco, USA",
              start_date: "Jun 2017",
              end_date: "Dec 2019",
              description_points: [
                "Designed and developed SAP Sweden payroll module for one-off tax processing.",
                "Enhanced SAP standard reports to support Concurrent Employment requirements in Sweden public sector."
              ]
            }
          ] : (baseData.experience || []).map(exp => ({
            position: exp.position || "",
            company: exp.company || "",
            location: exp.location || "",
            start_date: exp.start_date || exp.start_year || "",
            end_date: exp.end_date || exp.end_year || "",
              description_points: extractExperienceBullets(exp)
          })),
          // Add consulting-specific fields
          industry_expertise: baseData.isTemplate
            ? baseData.industry_expertise || categorizedSkills.industry
            : (baseData.industry_expertise && baseData.industry_expertise.length > 0
                ? normalizeArray(baseData.industry_expertise)
                : categorizedSkills.industry),
          functional_expertise: baseData.isTemplate
            ? baseData.functional_expertise || categorizedSkills.functional
            : (baseData.functional_expertise && baseData.functional_expertise.length > 0
                ? normalizeArray(baseData.functional_expertise)
                : categorizedSkills.functional),
          programming_languages: baseData.isTemplate
            ? baseData.programming_languages || categorizedSkills.programming
            : (baseData.programming_languages && baseData.programming_languages.length > 0
                ? normalizeArray(baseData.programming_languages)
                : categorizedSkills.programming),
          databases: baseData.isTemplate
            ? baseData.databases || categorizedSkills.databases
            : (baseData.databases && baseData.databases.length > 0
                ? normalizeArray(baseData.databases)
                : categorizedSkills.databases),
          data_analytics_tools: baseData.isTemplate
            ? baseData.data_analytics_tools || categorizedSkills.analytics
            : (baseData.data_analytics_tools && baseData.data_analytics_tools.length > 0
                ? normalizeArray(baseData.data_analytics_tools)
                : categorizedSkills.analytics),
          data_science_tools: baseData.isTemplate
            ? baseData.data_science_tools || categorizedSkills.dataScience
            : (baseData.data_science_tools && baseData.data_science_tools.length > 0
                ? normalizeArray(baseData.data_science_tools)
                : categorizedSkills.dataScience),
          big_data_platforms: baseData.isTemplate
            ? baseData.big_data_platforms || categorizedSkills.bigData
            : (baseData.big_data_platforms && baseData.big_data_platforms.length > 0
                ? normalizeArray(baseData.big_data_platforms)
                : categorizedSkills.bigData),
          key_skills: baseData.isTemplate ? [
            "Energy", "Supply Chain / Logistics", "Software & High Tech",
            "Data Analytics & Visualization", "Data Science", "Big Data Platforms",
            "Programming Languages", "Databases", "Tableau / Power BI",
            "Pandas / Scikit / PyTorch", "Spark / Hadoop",
            "Angular", "React", "SQL Server", "Oracle"
          ] : normalizeArray([
            ...(baseData.key_skills || []),
            ...categorizedSkills.industry,
            ...categorizedSkills.functional,
            ...categorizedSkills.programming,
            ...categorizedSkills.databases,
            ...categorizedSkills.analytics,
            ...categorizedSkills.dataScience,
            ...categorizedSkills.bigData,
          ]),
          // Add consulting-specific sections only for templates
          ...(baseData.isTemplate && {
            case_studies: [],
            certifications: [
              { name: "Agile CSM - 2023", provider: "" },
              { name: "AZ900 - 2023", provider: "" }
            ],
            education: [
              {
                degree: "B.S. Computer Science",
                institution: "Engineering, University Name",
                location: "",
                start_year: "",
                end_year: "",
                description: ""
              }
            ]
          })
        };
      case "tcs":
        // Helper function to convert uploaded skills to skills_matrix format
        const convertSkillsToMatrix = (skills) => {
          if (!skills) return [];
          
          // Handle structured skills object
          if (typeof skills === 'object' && !Array.isArray(skills)) {
            const allSkills = [
              ...(skills.programming_languages || []),
              ...(skills.frameworks || []),
              ...(skills.tools || []),
              ...(skills.ai_ml_technologies || [])
            ];
            return allSkills.map(skill => ({
              skill_name: skill,
              years_experience: "" // Leave empty for user to fill
            }));
          }
          
          // Handle array of skills
          if (Array.isArray(skills)) {
            return skills.map(skill => ({
              skill_name: typeof skill === 'string' ? skill : (skill.name || skill.skill_name || ""),
              years_experience: skill.years_experience || skill.years || ""
            }));
          }
          
          return [];
        };

        // If data is uploaded (not template), map skills to skills_matrix
        const mappedSkillsMatrix = baseData.isUploadedResume && baseData.skills
          ? convertSkillsToMatrix(baseData.skills)
          : (baseData.skills_matrix || [
              { skill_name: "Devops", years_experience: "5.2 Years" },
              { skill_name: "AWS", years_experience: "5 Years" },
              { skill_name: "Terraform", years_experience: "4 Years" }
            ]);

        // TCS template only needs specific fields - filter out irrelevant sections
        return {
          // Only include TCS-specific fields
          template_name: "tcs",
          isTemplate: baseData.isTemplate,
          isUploadedResume: baseData.isUploadedResume,
          // Candidate Details
          full_name: baseData.full_name || (baseData.isTemplate ? "Tamilarasan R" : ""),
          phone: baseData.phone || (baseData.isTemplate ? "9600535931" : ""),
          email: baseData.email || (baseData.isTemplate ? "tamildevopscloud@gmail.com" : ""),
          linkedin: baseData.linkedin || "",
          location: baseData.location || (baseData.isTemplate ? "Bangalore" : ""),
          former_tcs_emp: baseData.former_tcs_emp || "No",
          emp_id: baseData.emp_id || "NO",
          // Interview Availability
          interview_availability: baseData.interview_availability || (baseData.isTemplate ? [
            { date: "3.11.2025", time_slot: "3:00 PM" },
            { date: "4.11.2025", time_slot: "3:00 PM" },
            { date: "5.11.2025", time_slot: "3:00 PM" }
          ] : []),
          // Skills Matrix
          skills_matrix: mappedSkillsMatrix,
          // Supplier Comments
          supplier_comments: baseData.supplier_comments || (baseData.isTemplate ? "We have screened this profile. He have good Experience in Devops Engineer with 5.2 years." : ""),
          supplier_note: baseData.supplier_note || (baseData.isTemplate ? "NOTE: if any career gaps or education gaps. Must have to mention here with the reason. Why they have career or education gap" : "")
          // Explicitly exclude: experience, projects, achievements, interests, hobbies, education, certifications, etc.
        };
      case "ats": {
        const defaultLookingTo = [
          "Train your workforce on latest AI (technical, non-technical, no-code/low-code)",
          "Upskill leadership & CXO's on strategic AI thinking & implementation",
          "Run impactful hackathons, masterclasses, webinars or innovation workshops globally",
        ];

        const defaultBulletSections = [
          {
            title:
              "Conducted scores of customized corporate and individual trainings in the AI fields such as",
            items: [
              "Generative AI, Autonomous Multi Agent AI Systems, 75+ RAG Architectures and Systems, Conversational AI, Voice and Speech AI, GPT, ChatGPT, Llama, Anthropic Claude, Cohere, X-AI, Gemini etc.",
              "Frameworks such as Hugging Face, Langchain, LangGraph, LangSmith, Neo4J; AI models and tools from OpenAI, Model Context Protocol (MCP), Amazon, Google, Meta, etc. for Junior | Mid-level | Senior talent | Directors, AVP/VP levels | CXOs | Board Members.",
            ],
          },
          {
            title:
              "Conducted multiple Highly Specialized AI Trainings to global corporations in the field of",
            items: [
              "Healthcare, Manufacturing, Banking & Finance, Insurance, Retail, Travel & Tourism, FMCG, Aviation, Defense, Government, Food & Beverages, Agriculture & Farming, IT, Construction, Education, Financial Services, Technology, Research & Development, Automotive, Aerospace, Engineering, Telecom, Media, Fashion & Entertainment, Chemical, Electrical & Electronics, Energy, Hospitality, Sports, Transport, Capital Markets etc.",
            ],
          },
          {
            title:
              "Ongoing-long term engagement as 'Chief AI Architect and Consultant' with the State Government of Sikkim & Prince-King of Sikkim, to implement the following, as per the directives by the Union Government.",
            items: [
              "Architecting the AI integration blueprint across all sectors in Sikkim.",
              "Developing & Implementing AI driven solutions and products for various sectors.",
              "Designing the AI ecosystem and modular architecture for AI Centre of Innovation – Gangtok and Gangtok AI Institutes, AI Centre of Excellence, AI BPO/KPO, AI Technical Education enhancements in ITI, Diploma colleges.",
              "Providing consulting to establish first AI University in Northeast.",
              "Overseeing AI development and capacity-building initiatives for state universities.",
              "Leading AI ethics frameworks and inclusive community design.",
              "Leading AI driven solutions state-wide, in the field of disaster management, organic farming, Tourism, Education & Ecological Development.",
              "Driving global AI partnerships and AI funding & grant strategies.",
              "Supporting pilot designs and technical validation.",
              "Driving Research, Publishing Research Papers, White Papers, Enabling IP, Patenting etc.",
            ],
          },
          {
            title:
              "Globally Recognized Oracle 23AI Corporate Trainer and Consultant. Conducted successful 50+ trainings, workshops, road shows and product development of Oracle 23AI on",
            items: [
              "Oracle Cloud Infrastructure (OCI)",
              "APEX",
              "SQL Loader with AI Services",
              "Database Actions",
              "PL/SQL with AI Services",
              "Oracle Machine Learning (OML)",
              "Oracle Graph Studio",
              "Oracle AI Data Transformer",
              "Oracle AI Services",
            ],
          },
          {
            title:
              "Globally Recognized PALANTIR Corporate Trainer and Consultant. Conducted successful 15+ trainings, workshops, road shows and product development of PALANTIR on",
            items: [
              "Palantir Foundry",
              "Palantir Ontology",
              "Palantir Gotham",
              "Palantir Apolo",
              "Palantir Data Pipeline",
              "Palantir AIP",
              "Palantir Use Cases",
              "Palantir Certifications for Data Engineers, Architects etc.",
              "Palantir AI Services",
            ],
          },
          {
            title:
              "One among handful of Computing Experts globally to gain hands-on experience in Quantum Computing, providing consulting to global firms and conducting Corporate Trainings. Have conducted Quantum Computing (QC) corporate trainings covering the following topics and more for Fintech Companies, BFSI, Manufacturing, Defense equipment's, Locomotives, Automotives and EV.",
            items: [
              "Super Position, Entanglement, Decoherence and Teleportation",
              "Industry and Sector applications of Quantum Computing",
              "Use of QC in Forecasting, Cryptography, Quantum Communication, Searching with Glover's Search Algorithm, Mapping Optimization",
              "QC Computing, Parallelism",
              "D-Wave Systems, QC security",
              "Wave-Particle Duality, Quantum-Bits (Q-BIT)",
            ],
          },
          {
            title: "AI Corporate Trainings conducted for",
            items: [
              "Basic Levels (Interns, Fresher Hires, Junior Staff)",
              "Intermediate Levels (Engineers, Developers, Senior Staff)",
              "Advanced Levels (Architects, SME's, Managers, Practitioners, Evangelists)",
              "Executive Levels (Directors, AVP's, VP's, C-Suite and Board Members)",
            ],
          },
          {
            title:
              "Conducted 1000's of technology trainings on the AI, Machine Learning (ML), Deep Learning (DL), NLP, Computer Vision (CV) tools and frameworks such as",
            items: [
              "Ollama, ONNX, Ǫuantization, Tokenization, Transformers, Docker, Kubernetes, Vector DB's – Pinecone, Milvus, Chroma DB, Weaviate, Ǫdrant, FAISS, ScaNN, HNSW, OpenSearch, ElasticSearch, Azure OpenAI, AWS, GCP, Model Context Protocol (MCP)",
            ],
          },
          {
            title:
              "Conducted 700+ corporate trainings in the following technological fields and areas",
            items: [
              "AWS - SageMaker, Bedrock, Opensearch / Kendra",
              "Azure AI, Azure Machine Learning, AWS AI",
              "Deploying and maintaining Open-Source Vector Databases such as - Milvus, Ǫdrant, FAISS, Chroma etc.",
              "Weaviate, Elastic Search, Neo4J",
              "DYNATRACE Advance level",
              "NEWRELIC AI",
              "Amaloc beginner to advanced level",
              "DeepSeek R1, Alibaba Ǫwen Model, Gemini, Claude",
              "NVIDIA GPUs and Networks, NVIDIA Omniverse",
              "Robotic AI, Palantir AI, Palantir Foundry, Palantir-AIP, Palantir-Gotham, Palantir-Apolo",
              "Moveworks AI, Knime AI, Vertex AI, Kore AI",
              "MLOps, AIOps, CloudOps, LLMOps, GitOps, ModelOps, FinOps, Enterprise AI",
              "AI MSFT TeamsJS, SWE-Bench",
              "Maximo, Cursor, Oracle 23AI, Windsurf, Claude",
              "Multi-Modal, Social Media through AI tools, Visual Media through AI tools, Content Creation, Email automation etc., using AI.",
              "Shortwave AI, Notion AI, Cohere, Claude, Meta Llama, Mem AI, Runway AI",
              "Model Context Protocol (MCP)",
              "Google Agent2Agent Protocol (A2A), Gemini Models, Google Agentspace",
              "Ataccama (Data AI), Snowflake",
            ],
          },
        ];

        const defaultTaglinePrimary = `[ Global AI Corporate Trainer, AI Architect, AI Consultant, AI Researcher, AI Product Dev, MCP, Gen
AI Expert, Machine Learning (ML), Deep Learning, Natural Language Processing, GPT, LLM,
Copilot, Huggingface, Langchain, Agentic AI, RAG, AI Agents, NVIDIA, Oracle 23AI, Moveworks, Knime,
Palantir, N8N, Vibe Code Tools, Cursor, Claude, Anthropic, Gemini, Azure AI, GCP, AWS ]`;

        const defaultTaglineSecondary = `[ IT Professional with 20+ years' experience | AI Product Development Experience of 13+ Years | AI
Global Corporate Training Experience of 9+ Years ]`;

        const defaultSummaryIntro = `With a proven track record of highly successful AI training engagements across Fortune 500 companies, startups, governments, and academic institutions in 10+ countries, I help teams at all levels — from engineers to CXOs — unlock the full potential of AI.`;

        const defaultExpertiseParagraph = `I bring hands-on expertise in delivering beginner to advanced AI trainings and programs, backed by deep work experience in AI and consulting experience in AI Strategy, AI Foundry, and Enterprise AI Transformation.`;

        const defaultCollaborationParagraph = `Available for collaboration in multiple capacities: As a freelance AI Trainer, Short term Contracts, Long Term Contracts, Train the Trainer, AI Management Training, Consulting, Build AI Apps & Tools, Can help setup Online/Offline Training business, AI Training material, content & ToC creation, Engaging with clients to scope, negotiate, and close training opportunities.`;

        const defaultPhoneCTA = `📞 Let's talk about how I can help you drive AI Trainings, innovation, and skilling at scale.`;
        const defaultEmailCTA = `📧 Reach out to me today!`;
        const defaultLinkedIn = `LinkedIn: www.linkedin.com/in/harsharao-ai/`;
        const defaultWhatsApp = `WhatsApp: +91 99164-80004 (Please leave a voice message or WhatsApp/text in case I'm unreachable or busy)`;

        const isTemplateData = baseData.isTemplate !== false;

        return {
          ...baseData,
          template_name: "ats",
          full_name: baseData.full_name || (isTemplateData ? "Harsha Rao" : baseData.full_name || ""),
          tagline_primary: isTemplateData
            ? defaultTaglinePrimary
            : baseData.tagline_primary || "",
          tagline_secondary: isTemplateData
            ? defaultTaglineSecondary
            : baseData.tagline_secondary || "",
          summary_intro: isTemplateData
            ? defaultSummaryIntro
            : baseData.summary_intro || "",
          looking_to_items: isTemplateData
            ? defaultLookingTo
            : baseData.looking_to_items || [],
          expertise_paragraph: isTemplateData
            ? defaultExpertiseParagraph
            : baseData.expertise_paragraph || "",
          collaboration_paragraph: isTemplateData
            ? defaultCollaborationParagraph
            : baseData.collaboration_paragraph || "",
          call_to_action_phone: isTemplateData
            ? defaultPhoneCTA
            : baseData.call_to_action_phone || "",
          call_to_action_email: isTemplateData
            ? defaultEmailCTA
            : baseData.call_to_action_email || "",
          linkedin: isTemplateData ? defaultLinkedIn : baseData.linkedin || "",
          whatsapp: isTemplateData ? defaultWhatsApp : baseData.whatsapp || "",
          key_bullet_sections: isTemplateData
            ? defaultBulletSections
            : baseData.key_bullet_sections || [],
        };
      }
      default:
        return baseData;
    }
  };
  useEffect(() => {
  if (externalResumeData?.__savedTemplate) {
    const savedSnapshot = cloneResumeData(externalResumeData);
    if (!savedSnapshot.template_name) {
      savedSnapshot.template_name = templateId || "professional";
    }
    if (!savedSnapshot.__savedTemplate) {
      savedSnapshot.__savedTemplate = true;
    }
    if (externalResumeData.__savedId && !savedSnapshot.__savedId) {
      savedSnapshot.__savedId = externalResumeData.__savedId;
    }
    setResumeData(savedSnapshot);
    calculateCompletion(savedSnapshot);
    return;
  }
   if (externalResumeData) {
  const parsed = externalResumeData;
      // Use the getTemplateData function for proper mapping
      let finalMappedData = getTemplateData(templateId, parsed);
      // Add additional fields that might be missing
      finalMappedData.template_name = templateId || "professional";
      finalMappedData.isTemplate = false;
      finalMappedData.full_name =
        parsed.personal_info?.full_name || parsed.full_name || "";
      finalMappedData.job_title =
        parsed.personal_info?.job_title ||
        parsed.job_title ||
        parsed.title ||
        ""; // Use empty string, not null
      finalMappedData.email = parsed.personal_info?.email || parsed.email || "";
      finalMappedData.phone = parsed.personal_info?.phone || parsed.phone || "";
      finalMappedData.location =
        parsed.personal_info?.location || parsed.location || "";
      finalMappedData.address =
        parsed.personal_info?.address || parsed.location || "";
      finalMappedData.github = fixSocialMediaUrl(
        parsed.personal_info?.github || parsed.github || ""
      );
      finalMappedData.linkedin = fixSocialMediaUrl(
        parsed.personal_info?.linkedin || parsed.linkedin || ""
      );
      finalMappedData.leetcode = fixSocialMediaUrl(
        parsed.personal_info?.leetcode || parsed.leetcode || ""
      );
      finalMappedData.hackerrank = fixSocialMediaUrl(
        parsed.personal_info?.hackerrank || parsed.hackerrank || ""
      );
      finalMappedData.professional_summary = cleanHtmlTags(
        parsed.professional_summary ||
          parsed.summary ||
          parsed.resume_summary ||
          ""
      );
      finalMappedData.skills = {
        programming_languages: Array.isArray(
          parsed.skills?.programming_languages
        )
          ? parsed.skills.programming_languages
          : (
              parsed.skills?.programming_languages ||
              parsed.skills?.technical_skills ||
              parsed.programming_languages ||
              ""
            )
              .toString()
              .split(/[,]/)
              .map((s) => s.trim())
              .filter((s) => s),
        tools: Array.isArray(parsed.skills?.tools)
          ? parsed.skills.tools
          : (
              parsed.skills?.tools ||
              parsed.skills?.technologies ||
              parsed.tools ||
              ""
            )
              .toString()
              .split(/[,]/)
              .map((s) => s.trim())
              .filter((s) => s),
        frameworks: Array.isArray(parsed.skills?.frameworks)
          ? parsed.skills.frameworks
          : (parsed.skills?.frameworks || parsed.frameworks || "")
              .toString()
              .split(/[,]/)
              .map((s) => s.trim())
              .filter((s) => s),
        ai_ml_technologies: Array.isArray(parsed.skills?.ai_ml_technologies)
          ? parsed.skills.ai_ml_technologies
          : (
              parsed.skills?.ai_ml_technologies ||
              parsed.skills?.ai_technologies ||
              parsed.ai_ml_technologies ||
              ""
            )
              .toString()
              .split(/[,]/)
              .map((s) => s.trim())
              .filter((s) => s),
      };
      finalMappedData.key_skills = Array.isArray(
        parsed.skills?.programming_languages
      )
        ? parsed.skills.programming_languages
        : (
            parsed.skills?.programming_languages ||
            parsed.skills?.technical_skills ||
            parsed.programming_languages ||
            ""
          )
            .toString()
            .split(/[,]/)
            .map((s) => s.trim())
            .filter((s) => s);
      finalMappedData.technical_skills = Array.isArray(parsed.skills?.tools)
        ? parsed.skills.tools
        : (
            parsed.skills?.tools ||
            parsed.skills?.technologies ||
            parsed.tools ||
            ""
          )
            .toString()
            .split(/[,]/)
            .map((s) => s.trim())
            .filter((s) => s);
      // 🔍 DEBUG: Check experience data from backend
      finalMappedData.relevant_experience = (
        parsed.relevant_experience ||
        parsed.experience ||
        parsed.work_experience ||
        []
      ).map((exp) => ({
        position: cleanHtmlTags(exp.title || exp.position || ""),
        company: cleanHtmlTags(exp.company || exp.employer || ""),
        location: cleanHtmlTags(exp.location || ""),
        start_date: exp.start_date || "",
        end_date: exp.end_date || "",
        description_points: Array.isArray(
          exp.responsibilities || exp.description_points || exp.achievements
        )
          ? (
              exp.responsibilities ||
              exp.description_points ||
              exp.achievements
            ).map((point) => cleanHtmlTags(point))
          : [],
      }));
      finalMappedData.education = (parsed.education || []).map((edu) => ({
        degree: edu.degree || edu.qualification || "",
        institute: edu.institute || edu.institution || edu.school || "",
        institution: edu.institute || edu.institution || edu.school || "",
        field_of_study: edu.field_of_study || "",
        location: edu.location || "",
        start_year: edu.start_year || edu.start_date || "",
        end_year: edu.end_year || edu.end_date || "",
        cgpa: edu.cgpa || edu.gpa || "", // NEW backend sends 'gpa', old sends 'cgpa'
        gpa: edu.gpa || edu.cgpa || "", // Keep both fields
        coursework: edu.coursework || [],
      }));
      finalMappedData.certifications = parsed.certifications || [];
      // Get all available GitHub links from parsed data
      const availableGithubLinks = getAvailableGithubLinks(parsed.projects);
      finalMappedData.projects = (parsed.projects || []).map((p) => {
        const projectTitle = cleanHtmlTags(p.title || p.name || ""); // NEW backend sends 'name', old sends 'title'
        // Use enhanced matching function with multiple strategies
        const matchedGithubLink = findMatchingGithubLink(
          projectTitle,
          availableGithubLinks,
          p
        );
        return {
          title: projectTitle,
          name: projectTitle,
        description: Array.isArray(p.description)
            ? p.description.map((desc) => cleanHtmlTags(desc))
          : p.description
            ? [cleanHtmlTags(p.description)]
            : [],
          link: matchedGithubLink,
        technologies: p.tech_stack || p.technologies || [],
        tech_stack: p.tech_stack || p.technologies || [],
          github_link: matchedGithubLink,
          year: p.year || "",
        };
      });
      finalMappedData.achievements = Array.isArray(parsed.achievements) 
        ? parsed.achievements.map((achievement) => cleanHtmlTags(achievement))
        : [];
      // ✅ NEW FIELDS - Languages, Interests, Publications, etc.
      // ✅ Transform languages to STRINGS (CRITICAL!)
      finalMappedData.languages = (parsed.languages || []).map((lang) => {
        if (typeof lang === "string") return lang;
        if (typeof lang === "object" && lang !== null) {
          const languageName = lang.language || lang.name || "";
          const proficiencyLevel = lang.proficiency || "";
          if (languageName && proficiencyLevel) {
            return `${languageName} (${proficiencyLevel})`;
          }
          return languageName || "";
        }
        return String(lang);
      });
      finalMappedData.interests = parsed.interests || [];
      finalMappedData.publications = parsed.publications || [];
      finalMappedData.volunteer_work = parsed.volunteer_work || [];
      finalMappedData.additional_info = parsed.additional_info || [];
      finalMappedData.websites = Object.values(parsed.links || {}).filter(
        Boolean
      );
  // ⏩ Preserve your template-specific logic
      if (templateId === "simple") {
    // Map AI-parsed data to Simple template structure - EXACTLY like Professional
    finalMappedData.highlights = finalMappedData.key_skills || [];
    finalMappedData.address = finalMappedData.location || "";
        finalMappedData.professional_summary =
          finalMappedData.professional_summary || "";
    // Map education data exactly like Professional
    finalMappedData.education = finalMappedData.education || [];
    // Map experience data exactly like Professional - convert description_points to duties
        finalMappedData.work_experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
      position: exp.position || "",
      company: exp.company || "",
      location: exp.location || "",
      start_date: exp.start_date || "",
      end_date: exp.end_date || "",
          duties: extractExperienceBullets(exp),
    }));
    // Keep ALL the AI-parsed data exactly as it comes from backend
    finalMappedData.skills = finalMappedData.skills;
    finalMappedData.projects = finalMappedData.projects;
    finalMappedData.achievements = finalMappedData.achievements;
    finalMappedData.certifications = finalMappedData.certifications;
    finalMappedData.github = finalMappedData.github;
    finalMappedData.linkedin = finalMappedData.linkedin;
    finalMappedData.leetcode = finalMappedData.leetcode;
    finalMappedData.hackerrank = finalMappedData.hackerrank;
    // Handle additional sections for uploaded resumes
    finalMappedData.additional_sections = {
          certifications: finalMappedData.certifications?.length
            ? finalMappedData.certifications
            : [],
          awards_honors: finalMappedData.achievements?.length
            ? finalMappedData.achievements
            : [],
      volunteer_experience: [],
          professional_affiliations: [],
    };
    // Clean up mapped fields
    delete finalMappedData.key_skills;
    delete finalMappedData.location;
        // ✅ Don't set default references for uploaded resumes
        if (finalMappedData.isTemplate) {
    finalMappedData.references_note = "References Available on request.";
        }
      } else if (templateId === "professional") {
    // Map AI-parsed data to Professional template structure
    // Backend returns skills as: { programming_languages: [], tools: [], frameworks: [], ai_ml_technologies: [] }
        if (
          finalMappedData.skills &&
          typeof finalMappedData.skills === "object"
        ) {
      // Keep structured skills format for Professional template
      finalMappedData.structured_skills = finalMappedData.skills;
      // Also keep arrays for backward compatibility
      const allSkills = [];
          if (finalMappedData.skills.programming_languages)
            allSkills.push(...finalMappedData.skills.programming_languages);
          if (finalMappedData.skills.tools)
            allSkills.push(...finalMappedData.skills.tools);
          if (finalMappedData.skills.frameworks)
            allSkills.push(...finalMappedData.skills.frameworks);
          if (finalMappedData.skills.ai_ml_technologies)
            allSkills.push(...finalMappedData.skills.ai_ml_technologies);
      finalMappedData.key_skills = allSkills;
      // Ensure all skills arrays exist and are properly initialized
          if (!finalMappedData.skills.programming_languages)
            finalMappedData.skills.programming_languages = [];
      if (!finalMappedData.skills.tools) finalMappedData.skills.tools = [];
          if (!finalMappedData.skills.frameworks)
            finalMappedData.skills.frameworks = [];
          if (!finalMappedData.skills.ai_ml_technologies)
            finalMappedData.skills.ai_ml_technologies = [];
    } else {
      // Fallback to old format
      finalMappedData.key_skills = finalMappedData.key_skills || [];
      // Initialize structured skills if they don't exist
      finalMappedData.skills = {
        programming_languages: [],
        tools: [],
        frameworks: [],
            ai_ml_technologies: [],
      };
    }
      } else if (templateId === "modern") {
        // Map AI-parsed data to Modern template structure - Handle structured skills from backend
    // Backend returns skills as: { programming_languages: [], tools: [], frameworks: [], ai_ml_technologies: [] }
        if (
          finalMappedData.skills &&
          typeof finalMappedData.skills === "object"
        ) {
      // Keep structured skills format for Modern template
      finalMappedData.structured_skills = finalMappedData.skills;
      // Also keep arrays for backward compatibility
      const allSkills = [];
          if (finalMappedData.skills.programming_languages)
            allSkills.push(...finalMappedData.skills.programming_languages);
          if (finalMappedData.skills.tools)
            allSkills.push(...finalMappedData.skills.tools);
          if (finalMappedData.skills.frameworks)
            allSkills.push(...finalMappedData.skills.frameworks);
          if (finalMappedData.skills.ai_ml_technologies)
            allSkills.push(...finalMappedData.skills.ai_ml_technologies);
      finalMappedData.professional_skills = allSkills;
      finalMappedData.technical_skills = allSkills;
    } else {
      // Fallback to old format
          finalMappedData.professional_skills =
            finalMappedData.key_skills || [];
          finalMappedData.technical_skills =
            finalMappedData.technical_skills || [];
        }
    finalMappedData.profile_image_url = "";
    finalMappedData.website = "";
    finalMappedData.languages = finalMappedData.languages || [];
    finalMappedData.references = finalMappedData.references || [];
    // Auto-generate job title if not present
        if (
          !finalMappedData.job_title ||
          finalMappedData.job_title.trim() === ""
        ) {
      // Try to get job title from experience positions
          if (
            finalMappedData.relevant_experience &&
            finalMappedData.relevant_experience.length > 0
          ) {
        const firstExperience = finalMappedData.relevant_experience[0];
            if (
              firstExperience.position &&
              firstExperience.position.trim() !== ""
            ) {
          finalMappedData.job_title = firstExperience.position;
        }
      }
      // If still no job title, try to generate from professional summary
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
        if (finalMappedData.professional_summary) {
          // Extract job title from professional summary using common patterns
              const summary =
                finalMappedData.professional_summary.toLowerCase();
          const jobTitlePatterns = [
            /(?:experienced|skilled|dedicated|passionate)\s+(\w+(?:\s+\w+){0,3})/,
            /(\w+(?:\s+\w+){0,3})\s+(?:with|having|specializing)/,
            /(?:as a|working as)\s+(\w+(?:\s+\w+){0,3})/,
                /(\w+(?:\s+\w+){0,3})\s+(?:professional|specialist|manager|developer|engineer|analyst)/,
          ];
          for (const pattern of jobTitlePatterns) {
            const match = summary.match(pattern);
            if (match && match[1]) {
                  const potentialTitle = match[1]
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
              if (potentialTitle.length > 3 && potentialTitle.length < 50) {
                finalMappedData.job_title = potentialTitle;
                break;
              }
            }
          }
        }
      }
      // If still no job title, use a default based on skills
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
            const skills = [
              ...(finalMappedData.key_skills || []),
              ...(finalMappedData.technical_skills || []),
            ];
        if (skills.length > 0) {
          // Map common skills to job titles
          const skillToJobTitle = {
                javascript: "Frontend Developer",
                react: "React Developer",
                "node.js": "Node.js Developer",
                python: "Python Developer",
                java: "Java Developer",
                sql: "Database Developer",
                aws: "Cloud Engineer",
                docker: "DevOps Engineer",
                kubernetes: "DevOps Engineer",
                marketing: "Marketing Specialist",
                sales: "Sales Representative",
                "project management": "Project Manager",
                leadership: "Team Leader",
                analytics: "Data Analyst",
                design: "UI/UX Designer",
              };
          for (const skill of skills) {
            const skillLower = skill.toLowerCase();
                for (const [skillPattern, jobTitle] of Object.entries(
                  skillToJobTitle
                )) {
              if (skillLower.includes(skillPattern)) {
                finalMappedData.job_title = jobTitle;
                break;
              }
            }
            if (finalMappedData.job_title) break;
          }
        }
      }
      // Final fallback
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
        finalMappedData.job_title = "Professional";
      }
    }
    // Map education data exactly like Professional - use institute field for Modern template
        // IMPORTANT: Keep ALL fields including coursework, cgpa, gpa, and any new fields from backend
        finalMappedData.education = (finalMappedData.education || []).map(
          (edu) => ({
            ...edu, // ✅ Keep ALL fields from backend (internships, coursework, etc.)
      degree: edu.degree || "",
      institution: edu.institute || edu.institution || "", // Modern template expects 'institution' field
      location: edu.location || "",
      start_year: edu.start_year || edu.start_date || "",
            end_year: edu.end_year || edu.end_date || "",
            cgpa: edu.cgpa || edu.gpa || "", // ✅ Keep CGPA
            gpa: edu.gpa || edu.cgpa || "", // ✅ Keep GPA
            coursework: edu.coursework || [], // ✅ Keep coursework
          })
        );
    // Map experience data exactly like Professional - Modern template expects 'experience' field
        finalMappedData.experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
      position: exp.position || "",
      company: exp.company || "",
      location: exp.location || "",
      start_date: exp.start_date || "",
      end_date: exp.end_date || "",
          description_points: extractExperienceBullets(exp),
    }));
    // Keep ALL the AI-parsed data exactly as it comes from backend
    finalMappedData.projects = finalMappedData.projects || [];
    finalMappedData.achievements = finalMappedData.achievements || [];
    finalMappedData.certifications = finalMappedData.certifications || [];
    finalMappedData.github = finalMappedData.github;
    finalMappedData.linkedin = finalMappedData.linkedin;
    finalMappedData.leetcode = finalMappedData.leetcode;
    finalMappedData.hackerrank = finalMappedData.hackerrank;
    // Clean up mapped fields
    delete finalMappedData.key_skills;
      } else if (
        [
          "security-manager",
          "computer-teacher",
          "pe-teacher",
          "science-teacher",
          "english-teacher",
        ].includes(templateId)
      ) {
    // Map AI-parsed data to Security/Teacher template structure - EXACTLY like Professional
        finalMappedData.work_experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
      position: exp.position || "",
      company: exp.company || "",
      location: exp.location || "",
      start_date: exp.start_date || "",
      end_date: exp.end_date || "",
      responsibilities: extractExperienceBullets(exp),
        }));
        // Map education data exactly like Professional - Security/Teacher templates expect 'institution' field
        // IMPORTANT: Keep ALL fields including coursework, cgpa, gpa, and any new fields from backend
        finalMappedData.education = (finalMappedData.education || []).map(
          (edu) => ({
            ...edu, // ✅ Keep ALL fields from backend (internships, coursework, etc.)
      degree: edu.degree || "",
            institution: edu.institute || edu.institution || "", // Security/Teacher templates expect 'institution' field
      location: edu.location || "",
      start_year: edu.start_year || edu.start_date || "",
      end_year: edu.end_year || edu.end_date || "",
            cgpa: edu.cgpa || edu.gpa || "", // ✅ Keep CGPA
            gpa: edu.gpa || edu.cgpa || "", // ✅ Keep GPA
            coursework: edu.coursework || [], // ✅ Keep coursework
            description: edu.description || "",
          })
        );
    // Keep ALL the AI-parsed data exactly as it comes from backend
    finalMappedData.projects = finalMappedData.projects;
    finalMappedData.achievements = finalMappedData.achievements;
    finalMappedData.certifications = finalMappedData.certifications;
    finalMappedData.github = finalMappedData.github;
    finalMappedData.linkedin = finalMappedData.linkedin;
    finalMappedData.leetcode = finalMappedData.leetcode;
    finalMappedData.hackerrank = finalMappedData.hackerrank;
        if (templateId === "security-manager") {
              // Map skills exactly like Modern template - Handle structured skills from backend
    // Backend returns skills as: { programming_languages: [], tools: [], frameworks: [], ai_ml_technologies: [] }
          if (
            finalMappedData.skills &&
            typeof finalMappedData.skills === "object"
          ) {
      // Keep structured skills format for Security template
      finalMappedData.structured_skills = finalMappedData.skills;
      // Also keep arrays for backward compatibility
      const allSkills = [];
            if (finalMappedData.skills.programming_languages)
              allSkills.push(...finalMappedData.skills.programming_languages);
            if (finalMappedData.skills.tools)
              allSkills.push(...finalMappedData.skills.tools);
            if (finalMappedData.skills.frameworks)
              allSkills.push(...finalMappedData.skills.frameworks);
            if (finalMappedData.skills.ai_ml_technologies)
              allSkills.push(...finalMappedData.skills.ai_ml_technologies);
               finalMappedData.professional_skills = allSkills;
         finalMappedData.soft_skills = finalMappedData.soft_skills || [];
       } else {
      // Fallback to old format
            finalMappedData.professional_skills =
              finalMappedData.key_skills || [];
      finalMappedData.soft_skills = finalMappedData.soft_skills || [];
    }
      finalMappedData.profile_image_url = "";
      finalMappedData.linkedin = "";
          // Don't wipe languages - preserve user data
          finalMappedData.languages = finalMappedData.languages || [];
      // Auto-generate job title if not present for Security template
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
        // Try to get job title from experience positions
            if (
              finalMappedData.relevant_experience &&
              finalMappedData.relevant_experience.length > 0
            ) {
          const firstExperience = finalMappedData.relevant_experience[0];
              if (
                firstExperience.position &&
                firstExperience.position.trim() !== ""
              ) {
            finalMappedData.job_title = firstExperience.position;
          }
        }
        // If still no job title, try to generate from professional summary
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          if (finalMappedData.professional_summary) {
            // Extract job title from professional summary using security-specific patterns
                const summary =
                  finalMappedData.professional_summary.toLowerCase();
            const securityJobTitlePatterns = [
              /(?:experienced|skilled|dedicated|passionate)\s+(\w+(?:\s+\w+){0,3})/,
              /(\w+(?:\s+\w+){0,3})\s+(?:with|having|specializing)/,
              /(?:as a|working as)\s+(\w+(?:\s+\w+){0,3})/,
                  /(\w+(?:\s+\w+){0,3})\s+(?:security|manager|officer|analyst|specialist|consultant)/,
            ];
            for (const pattern of securityJobTitlePatterns) {
              const match = summary.match(pattern);
              if (match && match[1]) {
                    const potentialTitle = match[1]
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                    if (
                      potentialTitle.length > 3 &&
                      potentialTitle.length < 50
                    ) {
                  finalMappedData.job_title = potentialTitle;
                  break;
                }
              }
            }
          }
        }
        // If still no job title, use a default based on security skills
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
              const skills = [
                ...(finalMappedData.key_skills || []),
                ...(finalMappedData.technical_skills || []),
              ];
          if (skills.length > 0) {
            // Map security-specific skills to job titles
            const securitySkillToJobTitle = {
                  cybersecurity: "Cybersecurity Specialist",
                  "network security": "Network Security Engineer",
                  "information security": "Information Security Analyst",
                  "penetration testing": "Penetration Tester",
                  "ethical hacking": "Ethical Hacker",
                  "security audit": "Security Auditor",
                  "incident response": "Incident Response Specialist",
                  "threat intelligence": "Threat Intelligence Analyst",
                  "vulnerability assessment": "Vulnerability Assessor",
                  "security compliance": "Security Compliance Officer",
                  firewall: "Network Security Engineer",
                  siem: "Security Operations Analyst",
                  "ids/ips": "Security Engineer",
                  "malware analysis": "Malware Analyst",
                  forensics: "Digital Forensics Specialist",
                  "risk assessment": "Security Risk Analyst",
                  "security policy": "Security Policy Manager",
                  "access control": "Access Control Specialist",
                  encryption: "Cryptography Specialist",
                  "security awareness": "Security Awareness Trainer",
                  "iso 27001": "Security Compliance Specialist",
                  nist: "Security Standards Specialist",
                  "pci dss": "PCI Compliance Specialist",
                  gdpr: "Data Protection Officer",
                  leadership: "Security Manager",
                  "team management": "Security Team Lead",
                  "project management": "Security Project Manager",
                };
            for (const skill of skills) {
              const skillLower = skill.toLowerCase();
                  for (const [skillPattern, jobTitle] of Object.entries(
                    securitySkillToJobTitle
                  )) {
                if (skillLower.includes(skillPattern)) {
                  finalMappedData.job_title = jobTitle;
                  break;
                }
              }
              if (finalMappedData.job_title) break;
            }
          }
        }
        // Final fallback for security
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          finalMappedData.job_title = "Security Professional";
        }
      }
      delete finalMappedData.key_skills;
        } else if (templateId === "computer-teacher") {
      finalMappedData.key_skills = finalMappedData.key_skills || [];
      // Auto-generate job title if not present for Tech/Computer Teacher template
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
        // Try to get job title from experience positions
            if (
              finalMappedData.relevant_experience &&
              finalMappedData.relevant_experience.length > 0
            ) {
          const firstExperience = finalMappedData.relevant_experience[0];
              if (
                firstExperience.position &&
                firstExperience.position.trim() !== ""
              ) {
            finalMappedData.job_title = firstExperience.position;
          }
        }
        // If still no job title, try to generate from professional summary
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          if (finalMappedData.professional_summary) {
            // Extract job title from professional summary using tech-specific patterns
                const summary =
                  finalMappedData.professional_summary.toLowerCase();
            const techJobTitlePatterns = [
              /(?:experienced|skilled|dedicated|passionate)\s+(\w+(?:\s+\w+){0,3})/,
              /(\w+(?:\s+\w+){0,3})\s+(?:with|having|specializing)/,
              /(?:as a|working as)\s+(\w+(?:\s+\w+){0,3})/,
                  /(\w+(?:\s+\w+){0,3})\s+(?:teacher|instructor|professor|lecturer|educator|developer|engineer|specialist|analyst)/,
            ];
            for (const pattern of techJobTitlePatterns) {
              const match = summary.match(pattern);
              if (match && match[1]) {
                    const potentialTitle = match[1]
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                    if (
                      potentialTitle.length > 3 &&
                      potentialTitle.length < 50
                    ) {
                  finalMappedData.job_title = potentialTitle;
                  break;
                }
              }
            }
          }
        }
        // If still no job title, use a default based on tech skills
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
              const skills = [
                ...(finalMappedData.key_skills || []),
                ...(finalMappedData.technical_skills || []),
              ];
          if (skills.length > 0) {
            // Map tech-specific skills to job titles
            const techSkillToJobTitle = {
                  "computer science": "Computer Science Teacher",
                  programming: "Programming Instructor",
                  coding: "Coding Teacher",
                  "software development": "Software Development Instructor",
                  "web development": "Web Development Teacher",
                  python: "Python Programming Teacher",
                  java: "Java Programming Teacher",
                  javascript: "JavaScript Teacher",
                  html: "Web Development Teacher",
                  css: "Web Design Teacher",
                  sql: "Database Teacher",
                  "data structures": "Computer Science Teacher",
                  algorithms: "Computer Science Teacher",
                  cybersecurity: "Cybersecurity Teacher",
                  networking: "Computer Networking Teacher",
                  "artificial intelligence": "AI Teacher",
                  "machine learning": "Machine Learning Teacher",
                  robotics: "Robotics Teacher",
                  stem: "STEM Teacher",
                  technology: "Technology Teacher",
                  "digital literacy": "Digital Literacy Teacher",
                  "computer applications": "Computer Applications Teacher",
                  "information technology": "IT Teacher",
                  "computer programming": "Computer Programming Teacher",
                  "software engineering": "Software Engineering Teacher",
                  database: "Database Teacher",
                  "cloud computing": "Cloud Computing Teacher",
                  devops: "DevOps Teacher",
                  agile: "Agile Development Teacher",
                  scrum: "Scrum Master Teacher",
                  "project management": "IT Project Management Teacher",
                };
            for (const skill of skills) {
              const skillLower = skill.toLowerCase();
                  for (const [skillPattern, jobTitle] of Object.entries(
                    techSkillToJobTitle
                  )) {
                if (skillLower.includes(skillPattern)) {
                  finalMappedData.job_title = jobTitle;
                  break;
                }
              }
              if (finalMappedData.job_title) break;
            }
          }
        }
        // Final fallback for tech/computer teacher
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          finalMappedData.job_title = "Computer Science Teacher";
        }
      }
        } else if (templateId === "english-teacher") {
      // Auto-generate job title if not present for English Teacher template
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
        // Try to get job title from experience positions
            if (
              finalMappedData.relevant_experience &&
              finalMappedData.relevant_experience.length > 0
            ) {
          const firstExperience = finalMappedData.relevant_experience[0];
              if (
                firstExperience.position &&
                firstExperience.position.trim() !== ""
              ) {
            finalMappedData.job_title = firstExperience.position;
          }
        }
        // If still no job title, try to generate from professional summary
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          if (finalMappedData.professional_summary) {
            // Extract job title from professional summary using English teacher-specific patterns
                const summary =
                  finalMappedData.professional_summary.toLowerCase();
            const englishJobTitlePatterns = [
              /(?:experienced|skilled|dedicated|passionate)\s+(\w+(?:\s+\w+){0,3})/,
              /(\w+(?:\s+\w+){0,3})\s+(?:with|having|specializing)/,
              /(?:as a|working as)\s+(\w+(?:\s+\w+){0,3})/,
                  /(\w+(?:\s+\w+){0,3})\s+(?:teacher|instructor|professor|lecturer|educator|specialist)/,
            ];
            for (const pattern of englishJobTitlePatterns) {
              const match = summary.match(pattern);
              if (match && match[1]) {
                    const potentialTitle = match[1]
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                    if (
                      potentialTitle.length > 3 &&
                      potentialTitle.length < 50
                    ) {
                  finalMappedData.job_title = potentialTitle;
                  break;
                }
              }
            }
          }
        }
        // If still no job title, use a default based on English teaching skills
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
              const skills = [
                ...(finalMappedData.key_skills || []),
                ...(finalMappedData.technical_skills || []),
              ];
          if (skills.length > 0) {
            // Map English teaching skills to job titles
            const englishSkillToJobTitle = {
                  english: "English Teacher",
                  literature: "Literature Teacher",
                  writing: "Writing Teacher",
                  grammar: "Grammar Teacher",
                  communication: "Communication Teacher",
                  reading: "Reading Teacher",
                  composition: "Composition Teacher",
                  "creative writing": "Creative Writing Teacher",
                  "academic writing": "Academic Writing Teacher",
                  "essay writing": "Essay Writing Teacher",
                  poetry: "Poetry Teacher",
                  drama: "Drama Teacher",
                  theater: "Theater Teacher",
                  "public speaking": "Public Speaking Teacher",
                  debate: "Debate Teacher",
                  journalism: "Journalism Teacher",
                  "media literacy": "Media Literacy Teacher",
                  "critical thinking": "Critical Thinking Teacher",
                  "analytical reading": "Analytical Reading Teacher",
                  research: "Research Teacher",
                  "academic skills": "Academic Skills Teacher",
                  "study skills": "Study Skills Teacher",
                  "test preparation": "Test Preparation Teacher",
                  "ap literature": "AP Literature Teacher",
                  "ib english": "IB English Teacher",
                  esl: "ESL Teacher",
                  ell: "English Language Learner Teacher",
                  bilingual: "Bilingual English Teacher",
                  multilingual: "Multilingual English Teacher",
                  elementary: "Elementary English Teacher",
                  "middle school": "Middle School English Teacher",
                  "high school": "High School English Teacher",
                  college: "College English Instructor",
                  university: "University English Professor",
                };
            for (const skill of skills) {
              const skillLower = skill.toLowerCase();
                  for (const [skillPattern, jobTitle] of Object.entries(
                    englishSkillToJobTitle
                  )) {
                if (skillLower.includes(skillPattern)) {
                  finalMappedData.job_title = jobTitle;
                  break;
                }
              }
              if (finalMappedData.job_title) break;
            }
          }
        }
        // Final fallback for English teacher
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          finalMappedData.job_title = "English Teacher";
        }
      }
        } else if (templateId === "pe-teacher") {
      // Auto-generate job title if not present for PE Teacher template
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
        // Try to get job title from experience positions
            if (
              finalMappedData.relevant_experience &&
              finalMappedData.relevant_experience.length > 0
            ) {
          const firstExperience = finalMappedData.relevant_experience[0];
              if (
                firstExperience.position &&
                firstExperience.position.trim() !== ""
              ) {
            finalMappedData.job_title = firstExperience.position;
          }
        }
        // If still no job title, try to generate from professional summary
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          if (finalMappedData.professional_summary) {
            // Extract job title from professional summary using PE teacher-specific patterns
                const summary =
                  finalMappedData.professional_summary.toLowerCase();
            const peJobTitlePatterns = [
              /(?:experienced|skilled|dedicated|passionate)\s+(\w+(?:\s+\w+){0,3})/,
              /(\w+(?:\s+\w+){0,3})\s+(?:with|having|specializing)/,
              /(?:as a|working as)\s+(\w+(?:\s+\w+){0,3})/,
                  /(\w+(?:\s+\w+){0,3})\s+(?:teacher|instructor|coach|trainer|educator|specialist)/,
            ];
            for (const pattern of peJobTitlePatterns) {
              const match = summary.match(pattern);
              if (match && match[1]) {
                    const potentialTitle = match[1]
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                    if (
                      potentialTitle.length > 3 &&
                      potentialTitle.length < 50
                    ) {
                  finalMappedData.job_title = potentialTitle;
                  break;
                }
              }
            }
          }
        }
        // If still no job title, use a default based on PE teaching skills
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
              const skills = [
                ...(finalMappedData.key_skills || []),
                ...(finalMappedData.technical_skills || []),
              ];
          if (skills.length > 0) {
            // Map PE teaching skills to job titles
            const peSkillToJobTitle = {
                  "physical education": "Physical Education Teacher",
                  pe: "Physical Education Teacher",
                  gym: "Physical Education Teacher",
                  fitness: "Fitness Teacher",
                  sports: "Sports Teacher",
                  athletics: "Athletics Teacher",
                  coaching: "Sports Coach",
                  swimming: "Swimming Instructor",
                  dance: "Dance Teacher",
                  yoga: "Yoga Teacher",
                  "martial arts": "Martial Arts Instructor",
                  health: "Health Teacher",
                  wellness: "Wellness Teacher",
                  nutrition: "Nutrition Teacher",
                  exercise: "Exercise Teacher",
                  "strength training": "Strength Training Teacher",
                  cardio: "Cardio Fitness Teacher",
                  "team sports": "Team Sports Teacher",
                  "individual sports": "Individual Sports Teacher",
                  "outdoor education": "Outdoor Education Teacher",
                  "adventure sports": "Adventure Sports Teacher",
                  recreation: "Recreation Teacher",
                  "motor skills": "Motor Skills Teacher",
                  coordination: "Coordination Teacher",
                  balance: "Balance Teacher",
                  flexibility: "Flexibility Teacher",
                  endurance: "Endurance Teacher",
                  speed: "Speed Training Teacher",
                  agility: "Agility Teacher",
                  power: "Power Training Teacher",
                  elementary: "Elementary PE Teacher",
                  "middle school": "Middle School PE Teacher",
                  "high school": "High School PE Teacher",
                  college: "College PE Instructor",
                  university: "University PE Professor",
                };
            for (const skill of skills) {
              const skillLower = skill.toLowerCase();
                  for (const [skillPattern, jobTitle] of Object.entries(
                    peSkillToJobTitle
                  )) {
                if (skillLower.includes(skillPattern)) {
                  finalMappedData.job_title = jobTitle;
                  break;
                }
              }
              if (finalMappedData.job_title) break;
            }
          }
        }
        // Final fallback for PE teacher
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          finalMappedData.job_title = "Physical Education Teacher";
        }
      }
        } else if (templateId === "science-teacher") {
      // Auto-generate job title if not present for Science Teacher template
          if (
            !finalMappedData.job_title ||
            finalMappedData.job_title.trim() === ""
          ) {
        // Try to get job title from experience positions
            if (
              finalMappedData.relevant_experience &&
              finalMappedData.relevant_experience.length > 0
            ) {
          const firstExperience = finalMappedData.relevant_experience[0];
              if (
                firstExperience.position &&
                firstExperience.position.trim() !== ""
              ) {
            finalMappedData.job_title = firstExperience.position;
          }
        }
        // If still no job title, try to generate from professional summary
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          if (finalMappedData.professional_summary) {
            // Extract job title from professional summary using science teacher-specific patterns
                const summary =
                  finalMappedData.professional_summary.toLowerCase();
            const scienceJobTitlePatterns = [
              /(?:experienced|skilled|dedicated|passionate)\s+(\w+(?:\s+\w+){0,3})/,
              /(\w+(?:\s+\w+){0,3})\s+(?:with|having|specializing)/,
              /(?:as a|working as)\s+(\w+(?:\s+\w+){0,3})/,
                  /(\w+(?:\s+\w+){0,3})\s+(?:teacher|instructor|professor|lecturer|educator|specialist)/,
            ];
            for (const pattern of scienceJobTitlePatterns) {
              const match = summary.match(pattern);
              if (match && match[1]) {
                    const potentialTitle = match[1]
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                    if (
                      potentialTitle.length > 3 &&
                      potentialTitle.length < 50
                    ) {
                  finalMappedData.job_title = potentialTitle;
                  break;
                }
              }
            }
          }
        }
        // If still no job title, use a default based on science teaching skills
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
              const skills = [
                ...(finalMappedData.key_skills || []),
                ...(finalMappedData.technical_skills || []),
              ];
          if (skills.length > 0) {
            // Map science teaching skills to job titles
            const scienceSkillToJobTitle = {
                  science: "Science Teacher",
                  biology: "Biology Teacher",
                  chemistry: "Chemistry Teacher",
                  physics: "Physics Teacher",
                  "earth science": "Earth Science Teacher",
                  "environmental science": "Environmental Science Teacher",
                  "life science": "Life Science Teacher",
                  "physical science": "Physical Science Teacher",
                  astronomy: "Astronomy Teacher",
                  geology: "Geology Teacher",
                  oceanography: "Oceanography Teacher",
                  meteorology: "Meteorology Teacher",
                  anatomy: "Anatomy Teacher",
                  physiology: "Physiology Teacher",
                  genetics: "Genetics Teacher",
                  ecology: "Ecology Teacher",
                  microbiology: "Microbiology Teacher",
                  botany: "Botany Teacher",
                  zoology: "Zoology Teacher",
                  biochemistry: "Biochemistry Teacher",
                  "organic chemistry": "Organic Chemistry Teacher",
                  "inorganic chemistry": "Inorganic Chemistry Teacher",
                  "analytical chemistry": "Analytical Chemistry Teacher",
                  "physical chemistry": "Physical Chemistry Teacher",
                  mechanics: "Mechanics Teacher",
                  thermodynamics: "Thermodynamics Teacher",
                  optics: "Optics Teacher",
                  electricity: "Electricity Teacher",
                  magnetism: "Magnetism Teacher",
                  waves: "Waves Teacher",
                  energy: "Energy Teacher",
                  matter: "Matter Teacher",
                  atoms: "Atomic Physics Teacher",
                  molecules: "Molecular Science Teacher",
                  cells: "Cell Biology Teacher",
                  ecosystems: "Ecosystems Teacher",
                  evolution: "Evolution Teacher",
                  climate: "Climate Science Teacher",
                  weather: "Weather Teacher",
                  rocks: "Geology Teacher",
                  minerals: "Mineralogy Teacher",
                  fossils: "Paleontology Teacher",
                  dinosaurs: "Paleontology Teacher",
                  space: "Space Science Teacher",
                  planets: "Planetary Science Teacher",
                  stars: "Astronomy Teacher",
                  galaxies: "Astronomy Teacher",
                  universe: "Cosmology Teacher",
                  elementary: "Elementary Science Teacher",
                  "middle school": "Middle School Science Teacher",
                  "high school": "High School Science Teacher",
                  college: "College Science Instructor",
                  university: "University Science Professor",
                };
            for (const skill of skills) {
              const skillLower = skill.toLowerCase();
                  for (const [skillPattern, jobTitle] of Object.entries(
                    scienceSkillToJobTitle
                  )) {
                if (skillLower.includes(skillPattern)) {
                  finalMappedData.job_title = jobTitle;
                  break;
                }
              }
              if (finalMappedData.job_title) break;
            }
          }
        }
        // Final fallback for science teacher
            if (
              !finalMappedData.job_title ||
              finalMappedData.job_title.trim() === ""
            ) {
          finalMappedData.job_title = "Science Teacher";
        }
      }
        } else {
          // For english-teacher, map key_skills to programming_languages in skills object
          if (templateId === "english-teacher") {
            finalMappedData.skills = {
              programming_languages: finalMappedData.key_skills || [],
              frameworks: [],
              tools: [],
              ai_ml_technologies: [],
            };
            // Preserve key_skills for compatibility
            finalMappedData.key_skills = finalMappedData.key_skills || [];
    } else {
      finalMappedData.skills = finalMappedData.key_skills || [];
      delete finalMappedData.key_skills;
    }
        }
        if (
          ["pe-teacher", "science-teacher", "english-teacher"].includes(
            templateId
          )
        ) {
      finalMappedData.address = finalMappedData.location || "";
      delete finalMappedData.location;
    }
    // Clean up mapped fields
      } else if (
        ["business-development", "cv-guy", "marketing-specialist"].includes(
          templateId
        )
      ) {
    // Map AI-parsed data to Business template structure - EXACTLY like Professional
        finalMappedData.experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
      position: exp.position || "",
      company: exp.company || "",
      location: exp.location || "",
      start_date: exp.start_date || "",
      end_date: exp.end_date || "",
          responsibilities: extractExperienceBullets(exp),
    }));
    finalMappedData.summary = finalMappedData.professional_summary || "";
        // For marketing-specialist, also preserve relevant_experience field
        if (templateId === "marketing-specialist") {
          finalMappedData.relevant_experience =
            finalMappedData.relevant_experience || [];
        }
    // Map education data exactly like Professional
    finalMappedData.education = finalMappedData.education || [];
    // Keep ALL the AI-parsed data exactly as it comes from backend
    finalMappedData.projects = finalMappedData.projects;
    finalMappedData.achievements = finalMappedData.achievements;
    finalMappedData.certifications = finalMappedData.certifications;
    finalMappedData.github = finalMappedData.github;
    finalMappedData.linkedin = finalMappedData.linkedin;
    finalMappedData.leetcode = finalMappedData.leetcode;
    finalMappedData.hackerrank = finalMappedData.hackerrank;
        if (templateId === "marketing-specialist") {
      finalMappedData.skills = {
        "Digital Marketing": finalMappedData.key_skills || [],
            Tools: finalMappedData.technical_skills || [],
      };
      finalMappedData.contact = {
        phone: finalMappedData.phone || "",
        email: finalMappedData.email || "",
            linkedin: "",
      };
      finalMappedData.job_title = "";
      finalMappedData.profile_image_url = "";
          // Keep the original data for frontend display
          // finalMappedData.key_skills, phone, email are preserved
        } else if (templateId === "business-development") {
      finalMappedData.address = finalMappedData.location || "";
      delete finalMappedData.location;
          // Map skills for business templates (similar to professional's structured_skills)
          if (
            finalMappedData.skills &&
            typeof finalMappedData.skills === "object"
          ) {
            // Preserve the structured skills object
        finalMappedData.structured_skills = finalMappedData.skills;
            // Combine all skills into core_competencies for display
        const allSkills = [];
            if (finalMappedData.skills.programming_languages)
              allSkills.push(...finalMappedData.skills.programming_languages);
            if (finalMappedData.skills.frameworks)
              allSkills.push(...finalMappedData.skills.frameworks);
            if (finalMappedData.skills.tools)
              allSkills.push(...finalMappedData.skills.tools);
            if (finalMappedData.skills.ai_ml_technologies)
              allSkills.push(...finalMappedData.skills.ai_ml_technologies);
            if (finalMappedData.key_skills)
              allSkills.push(...finalMappedData.key_skills); // Include key_skills if present
            finalMappedData.core_competencies = allSkills.filter(Boolean); // Filter out any null/undefined
            finalMappedData.tools_tech = finalMappedData.skills.tools || []; // Explicitly map tools_tech
      } else {
            // Fallback if skills is not an object (e.g., just key_skills array)
            finalMappedData.core_competencies =
              finalMappedData.key_skills || [];
            finalMappedData.tools_tech = finalMappedData.technical_skills || []; // Use technical_skills as fallback for tools_tech
          }
          // Do NOT delete finalMappedData.key_skills here, as it might be used as a fallback in the template's displayData.
          // The template's displayData handles the priority.
        } else if (templateId === "cv-guy") {
          finalMappedData.address = finalMappedData.location || "";
          delete finalMappedData.location;
          // Map skills for cv-guy template - use simple skills array
          if (
            finalMappedData.skills &&
            typeof finalMappedData.skills === "object"
          ) {
            // Combine all structured skills into a simple array for cv-guy
            const allSkills = [];
            if (finalMappedData.skills.programming_languages)
              allSkills.push(...finalMappedData.skills.programming_languages);
            if (finalMappedData.skills.frameworks)
              allSkills.push(...finalMappedData.skills.frameworks);
            if (finalMappedData.skills.tools)
              allSkills.push(...finalMappedData.skills.tools);
            if (finalMappedData.skills.ai_ml_technologies)
              allSkills.push(...finalMappedData.skills.ai_ml_technologies);
            if (finalMappedData.key_skills)
              allSkills.push(...finalMappedData.key_skills); // Include key_skills if present
            finalMappedData.skills = allSkills.filter(Boolean); // Set as simple array
            // Keep key_skills as fallback for preview compatibility
            finalMappedData.key_skills = allSkills.filter(Boolean);
          } else {
            // Fallback if skills is not an object (e.g., just key_skills array)
            finalMappedData.skills = finalMappedData.key_skills || [];
            // Don't delete key_skills as it's used by preview
          }
          // Keep professional_summary for form/preview compatibility
          finalMappedData.professional_summary =
            finalMappedData.professional_summary || finalMappedData.summary;
        } else {
          // This else block handles other cases within the outer if,
          // ensuring skills is an array if not handled by specific template logic.
          finalMappedData.skills = finalMappedData.key_skills || [];
          delete finalMappedData.key_skills;
        }
        // Clean up mapped fields - but preserve fields needed by cv-guy, marketing-specialist, and teacher templates
        if (
          templateId !== "cv-guy" &&
          templateId !== "marketing-specialist" &&
          ![
            "pe-teacher",
            "science-teacher",
            "english-teacher",
            "computer-teacher",
          ].includes(templateId)
        ) {
          delete finalMappedData.professional_summary;
        }
        // ✅ Keep relevant_experience for teacher templates (form needs it for prefilling)
        if (
          templateId !== "marketing-specialist" &&
          !["pe-teacher", "science-teacher", "english-teacher", "computer-teacher"].includes(templateId)
        ) {
          // Keep relevant_experience for cross-template consistency
      }
      }
      setResumeData(finalMappedData);
      calculateCompletion(finalMappedData);
      return;
} else {
      const initialData = getTemplateData(templateId || "professional", null);
    setResumeData(initialData);
    calculateCompletion(initialData);
      if (process.env.NODE_ENV !== "production") {
        console.log(`[ATS Upload] initial template data for ${templateId}`, initialData);
      }
      return;
  }
  }, [templateId, externalResumeData]);
  const handleUpdate = (update) => {
    const data = typeof update === "function" ? update(resumeData || {}) : update;
    const existingSavedId = resumeData?.__savedId;
    if (existingSavedId && data) {
      if (!data.__savedId) {
        data.__savedId = existingSavedId;
      }
      if (data.__savedId === existingSavedId) {
        data.__savedTemplate = true;
      }
    }
    if (data && !data.template_name) {
      data.template_name = templateId || "professional";
    }
    // Ensure skills data structure is maintained
    if (data.skills && typeof data.skills === "object") {
      // Ensure all skills arrays exist
      if (!data.skills.programming_languages)
        data.skills.programming_languages = [];
      if (!data.skills.tools) data.skills.tools = [];
      if (!data.skills.frameworks) data.skills.frameworks = [];
      if (!data.skills.ai_ml_technologies) data.skills.ai_ml_technologies = [];
      // Update key_skills to maintain consistency
      const allSkills = [];
      Object.values(data.skills).forEach((skillArray) => {
        if (Array.isArray(skillArray)) {
          allSkills.push(...skillArray);
        }
      });
      data.key_skills = allSkills;
    }
    setResumeData(data);
    calculateCompletion(data);
    if (activeField) {
      const newErrors = { ...validationErrors };
      if (newErrors[activeField]) {
        delete newErrors[activeField];
        setValidationErrors(newErrors);
      }
    }
  };
  const handleFieldFocus = (fieldName) => {
    setActiveField(fieldName);
    const newErrors = { ...validationErrors };
    if (newErrors[fieldName]) {
      delete newErrors[fieldName];
      setValidationErrors(newErrors);
    }
  };
  const handleFieldBlur = (fieldName) => {
    setActiveField(null);
    // Validate the field without overriding the data
    validateSingleField(resumeData, fieldName);
  };
  const calculateCompletion = (data) => {
    if (!data) return;
    let totalFields = 8;
    let filledFields = 0;
    const check = (field) => {
      if (field && String(field).trim()) filledFields++;
    };
    const checkArray = (arr) => {
      if (
        arr &&
        arr.length > 0 &&
        arr.some((item) => {
          if (typeof item === "string") return item.trim() !== "";
          if (typeof item === "object")
            return Object.values(item).some(
              (val) =>
                val !== null &&
                (typeof val === "string" ? val.trim() !== "" : true)
            );
        return false;
        })
      )
        filledFields++;
    };
    check(data.full_name);
    check(data.email);
    check(data.phone);
    check(data.location || data.address);
    check(data.professional_summary || data.summary);
    const exp =
      data.relevant_experience || data.work_experience || data.experience;
    checkArray(exp);
    checkArray(data.education);
    const skills =
      data.key_skills ||
      data.professional_skills ||
      data.skills ||
      data.highlights; // Check for generic 'skills' too
    // Special handling for marketing-specialist skills object
    if (
      templateId === "marketing-specialist" &&
      typeof skills === "object" &&
      !Array.isArray(skills)
    ) {
      if (
        Object.values(skills).some(
          (arr) => Array.isArray(arr) && arr.length > 0
        )
      ) {
        filledFields++;
      }
    } else {
      checkArray(skills);
    }
    setCompletionPercentage(
      totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0
    );
  };
  const validateSingleField = (data, fieldName) => {
    const newErrors = { ...validationErrors };
    delete newErrors[fieldName];
    // For templates with a 'contact' object, extract the fields
    let emailToCheck = data.email;
    let phoneToCheck = data.phone;
    if (data.contact && typeof data.contact === "object") {
        emailToCheck = data.contact.email;
        phoneToCheck = data.contact.phone;
    }
    switch (fieldName) {
      case "full_name":
        const name = data.full_name || "";
        if (!name.trim()) {
          newErrors.full_name = "Full name is required";
        } else if (/\d/.test(name)) {
          newErrors.full_name = "Name cannot contain numbers";
        }
        break;
      case "email":
      case "contact.email": // For marketing-specialist
        if (!emailToCheck || !emailToCheck.trim()) {
          newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToCheck)) {
          newErrors.email = "Please enter a valid email address";
        }
        break;
      case "phone":
      case "contact.phone": // For marketing-specialist
        if (!phoneToCheck || !phoneToCheck.trim()) {
          newErrors.phone = "Phone is required";
        }
        break;
      default:
        break;
    }
    setValidationErrors(newErrors);
  };
  const validateResume = (data) => {
    const errors = {};
    const addError = (field, message) => {
      if (!errors[field]) errors[field] = message;
    };
    const name = data.full_name || "";
    if (!name.trim()) addError("full_name", "Full name is required");
    else if (/\d/.test(name))
      addError("full_name", "Name cannot contain numbers");
    // Handle email and phone based on template structure (direct or nested in contact)
    let emailToValidate = data.email;
    let phoneToValidate = data.phone;
    if (data.contact && typeof data.contact === "object") {
        emailToValidate = data.contact.email;
        phoneToValidate = data.contact.phone;
    }
    if (!emailToValidate || !emailToValidate.trim())
      addError("email", "Email is required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToValidate))
      addError("email", "Please enter a valid email address");
    if (!phoneToValidate || !phoneToValidate.trim())
      addError("phone", "Phone is required");
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // NEW: Process highlighting markers from AI response
  const processHighlighting = (text) => {
    if (!text || typeof text !== "string") return text;
    // Convert HIGHLIGHT_START and HIGHLIGHT_END markers to JSX
    const parts = text.split(
      /(\*\*HIGHLIGHT_START\*\*.*?\*\*HIGHLIGHT_END\*\*)/g
    );
    return parts.map((part, index) => {
      if (part.includes("HIGHLIGHT_START") && part.includes("HIGHLIGHT_END")) {
        // Extract the highlighted content
        const content = part
          .replace(/\*\*HIGHLIGHT_START\*\*/g, "")
          .replace(/\*\*HIGHLIGHT_END\*\*/g, "");
        return (
          <span 
            key={index} 
            style={{ 
              fontWeight: "bold",
              color: "#000",
            }}
          >
            {content}
          </span>
        );
      }
      return part;
    });
  };
  // NEW: Process arrays with highlighting
  const processArrayWithHighlighting = (array) => {
    if (!Array.isArray(array)) return array;
    return array.map((item) => {
      if (typeof item === "string") {
        return item;
      }
      return item;
    });
  };
  const handleJDUpload = (file) => {
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        setTailoredError(
          "Please upload a valid PDF, DOC, or DOCX file for the job description."
        );
        return;
      }
      setJdFile(file);
      setTailoredError("");
    }
  };
  const handleJDTextChange = (text) => {
    setJdText(text);
    setJdFile(null); // Clear file when text is entered
    setTailoredError("");
  };
  const handleGenerateTailoredResume = async () => {
    if (!jdFile && !jdText.trim()) {
      setTailoredError(
        "Please upload a job description file or paste the job description text."
      );
      return;
    }
    setTailoredError("");
    setIsGeneratingTailored(true);
    try {
      const formData = new FormData();
      if (jdFile) {
        formData.append("jd", jdFile);
      } else if (jdText.trim()) {
        formData.append("jd_text", jdText.trim());
      }
      formData.append("resume_data", JSON.stringify(externalResumeData));
      formData.append("template_id", templateId);
      const response = await fetch("/api/create-tailored-resume", {
        method: "POST",
        body: formData,
        mode: "cors",
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      // Process the tailored data through the same mapping as the original data
      const parsed = data;
      // NEW: Apply highlighting to JD-matching content
      const processedData = {
        ...parsed,
        // Process professional summary with highlighting
        professional_summary:
          parsed.professional_summary || parsed.summary || "",
        // Process experience with highlighting
        experience:
          parsed.experience?.map((exp) => ({
          ...exp,
            responsibilities: exp.responsibilities || [],
        })) || [],
        // Process projects with highlighting
        projects:
          parsed.projects?.map((proj) => ({
          ...proj,
          description: proj.description || [],
            tech_stack: proj.tech_stack || [],
        })) || [],
        // Process skills with highlighting
        skills: {
          programming_languages: parsed.skills?.programming_languages || [],
          frameworks: parsed.skills?.frameworks || [],
          tools: parsed.skills?.tools || [],
          ai_ml_technologies: parsed.skills?.ai_ml_technologies || [],
        },
      };
      let finalMappedData = {
        template_name: templateId || "professional",
        isTemplate: false, // mark as uploaded resume
        isUploadedResume: true, // enable dynamic sections
        full_name: parsed.personal_info?.full_name || "",
        email: parsed.personal_info?.email || "",
        phone: parsed.personal_info?.phone || "",
        location: parsed.personal_info?.location || "",
        address: parsed.personal_info?.address || "",
        github: parsed.personal_info?.github || "",
        linkedin: parsed.personal_info?.linkedin || "",
        leetcode: parsed.personal_info?.leetcode || "",
        hackerrank: parsed.personal_info?.hackerrank || "",
        professional_summary: processedData.professional_summary || "",
        skills: processedData.skills,
        key_skills: processedData.skills?.programming_languages || [],
        technical_skills: processedData.skills?.tools || [],
        relevant_experience: (
          parsed.relevant_experience ||
          processedData.experience ||
          parsed.experience ||
          []
        ).map((exp) => ({
          position: exp.title || exp.position || "",
          company: exp.company || "",
          location: exp.location || "",
          start_date: exp.start_date || "",
          end_date: exp.end_date || "",
          description_points: exp.responsibilities || [], // Already processed in processedData
        })),
        education: (parsed.education || []).map((edu) => ({
          degree: edu.degree || "",
          institute: edu.institute || edu.institution || "",
          institution: edu.institute || edu.institution || "",
          field_of_study: edu.field_of_study || "",
          location: edu.location || "",
          start_year: edu.start_year || edu.start_date || "",
          end_year: edu.end_year || edu.end_date || "",
          cgpa: edu.cgpa || "",
          coursework: edu.coursework || [],
        })),
        certifications: parsed.certifications || [],
        projects: (processedData.projects || parsed.projects || []).map((p) => {
          const projectTitle = p.title || "";
          // Get available GitHub links for this specific data set
          const availableGithubLinks = getAvailableGithubLinks(
            processedData.projects || parsed.projects
          );
          // Use enhanced matching function with multiple strategies
          const matchedGithubLink = findMatchingGithubLink(
            projectTitle,
            availableGithubLinks,
            p
          );
          return {
            title: projectTitle,
            name: projectTitle,
          description: p.description || [], // Already processed in processedData
            link: matchedGithubLink,
          technologies: p.tech_stack || p.technologies || [], // Already processed in processedData
          tech_stack: p.tech_stack || p.technologies || [], // Already processed in processedData
          year: p.year || "",
            github_link: matchedGithubLink,
          };
        }),
        achievements: parsed.achievements || [],
        websites: Object.values(parsed.links || {}).filter(Boolean),
      };
      // Apply template-specific logic for tailored data
      if (templateId === "simple") {
        finalMappedData.highlights = finalMappedData.key_skills || [];
        finalMappedData.address = finalMappedData.location || "";
        finalMappedData.professional_summary =
          finalMappedData.professional_summary || "";
        finalMappedData.education = finalMappedData.education || [];
        finalMappedData.work_experience = (
          finalMappedData.relevant_experience || []
        ).map((exp) => ({
          position: exp.position || "",
          company: exp.company || "",
          location: exp.location || "",
          start_date: exp.start_date || "",
          end_date: exp.end_date || "",
          duties: extractExperienceBullets(exp),
        }));
        finalMappedData.skills = finalMappedData.skills;
        finalMappedData.projects = finalMappedData.projects;
        finalMappedData.achievements = finalMappedData.achievements;
        finalMappedData.certifications = finalMappedData.certifications;
      }
      setTailoredResumeData(finalMappedData);
      setShowTailoredResume(true);
      setShowJDUpload(false);
    } catch (error) {
      setTailoredError("Failed to create tailored resume. Please try again.");
    } finally {
      setIsGeneratingTailored(false);
    }
  };
  const handleTailoredResumeClick = () => {
    setShowJDUpload(true);
    setShowTailoredResume(false);
    setTailoredResumeData(null);
  };
  // 🎯 INTELLIGENT DYNAMIC SECTIONS
  // Only create tabs for fields that are NOT already displayed in base sections
  const getDynamicSections = () => {
    // Don't show dynamic sections for template data
    if (
      !resumeData ||
      !resumeData.isUploadedResume ||
      resumeData.isTemplate !== false
    ) {
      return [];
    }
    const dynamicSections = [];
    // 🔥 THE SMART LIST: Fields that are ALREADY DISPLAYED in base form sections
    // These should NEVER get their own tabs because the data is already shown!
    const fieldsAlreadyDisplayed = [
      // Contact section displays these
      "full_name",
      "name",
      "email",
      "phone",
      "location",
      "address",
      "job_title",
      "linkedin",
      "github",
      "portfolio",
      "hackerrank",
      "leetcode",
      "whatsapp",
      "websites",
      "personal_info",
      "personalinfo",
      "contact_info",
      "contactinfo",
      "contact",
      // Summary section displays these
      "professional_summary",
      "summary",
      "resume_summary",
      "tagline_primary",
      "tagline_secondary",
      "summary_intro",
      "looking_to_items",
      "expertise_paragraph",
      "collaboration_paragraph",
      "call_to_action_phone",
      "call_to_action_email",
      "key_bullet_sections",
      // Experience section displays these
      "relevant_experience",
      "work_experience",
      "experience",
      // Skills section displays these (ALL variations!)
      "skills",
      "key_skills",
      "technical_skills",
      "professional_skills",
      "structured_skills",
      "soft_skills",
      "programming_skills",
      // Education section displays these
      "education",
      // Certifications section displays these
      "certifications",
      // 🔥 INTERNAL DATA - Never show as sections
      "languages_data",
      "isTemplate",
      "isUploadedResume",
      "shouldShowUserData",
      "template_id",
      "templateId",
      "_sections_map",
      "dynamic_sections",
      // Projects section displays these
      "projects",
      // Achievements section displays these
      "achievements",
      "awards",
      // TCS template specific fields
      "interview_availability",
      "skills_matrix",
      "supplier_comments",
      "supplier_note",
      "former_tcs_emp",
      "emp_id",
      // Metadata fields (never show these)
      "isTemplate",
      "isUploadedResume",
      "shouldShowUserData",
      "template_name",
      "template_id",
      "profile_image_url",
      "references_note",
      // Simple template specific - don't show as dynamic tab
      "additional_sections",
      "work_experience",
      "highlights",
      "duties",
    ];
    // Helper to normalize field names for comparison (remove spaces, underscores, lowercase)
    const normalize = (str) => str.toLowerCase().replace(/[_\s-]/g, "");
    // Normalize the "already displayed" list
    const normalizedDisplayed = fieldsAlreadyDisplayed.map(normalize);
    // Helper to check if data actually exists and is not empty
    const hasData = (value) => {
      if (!value) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "string") return value.trim().length > 0;
      if (typeof value === "object") return Object.keys(value).length > 0;
      return true;
    };
    // Loop through ALL fields in resumeData
    Object.keys(resumeData).forEach((key) => {
      const normalizedKey = normalize(key);
      // Skip if this field is already displayed in a base section
      if (normalizedDisplayed.includes(normalizedKey)) {
        return;
      }
      // Only create section if it has actual data
      if (hasData(resumeData[key])) {
        // Format field name to readable label
        const label = key
          .split(/[_\s-]/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        dynamicSections.push({ id: key, label: label });
      }
    });
    return dynamicSections;
  };
  // 🎯 RENDER DYNAMIC SECTION FORMS
  // Automatically creates appropriate form fields based on data type
  const renderDynamicSectionForm = (sectionId) => {
    const fieldData = resumeData[sectionId];
    if (!fieldData) return null;
    // Update field helper
    const updateDynamicField = (value) => {
      handleUpdate({ ...resumeData, [sectionId]: value });
    };
    // Format section name for display
    const sectionLabel = sectionId
      .split(/[_\s-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    // Special handling for 'languages' - always render as simple text list since backend sends strings
    if (
      sectionId === "languages" &&
      Array.isArray(fieldData) &&
      fieldData.length > 0
    ) {
      // Languages are already formatted as strings like "English (Professional)"
      // Render with professional card styling matching base sections
      return (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">{sectionLabel}</h3>
            <button
              type="button"
              onClick={() => {
                updateDynamicField([...fieldData, ""]);
              }}
              className="add-button"
            >
              + Add Language
            </button>
          </div>
          {fieldData.map((lang, index) => (
            <div key={index} className="experience-card">
              <div className="card-header">
                <h4>Language {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => {
                    const updated = fieldData.filter((_, i) => i !== index);
                    updateDynamicField(updated);
                  }}
                  className="remove-button"
                >
                  🗑️
                </button>
              </div>
              <div className="card-content">
                <div className="form-group">
                  <input
                    type="text"
                    value={lang}
                    onChange={(e) => {
                      const updated = [...fieldData];
                      updated[index] = e.target.value;
                      updateDynamicField(updated);
                    }}
                    placeholder="e.g., English (Professional)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    // ARRAY handling - works for ANY array field (Interests, Publications, etc.)
    if (Array.isArray(fieldData)) {
      return (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">{sectionLabel}</h3>
            <button
              type="button"
              onClick={() => {
                updateDynamicField([...fieldData, ""]);
              }}
              className="add-button"
            >
              + Add {sectionLabel}
            </button>
          </div>
          {fieldData.map((item, index) => (
            <div key={index} className="experience-card">
              <div className="card-header">
                <h4>{sectionLabel} {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => {
                    const updated = fieldData.filter((_, i) => i !== index);
                    updateDynamicField(updated);
                  }}
                  className="remove-button"
                >
                  🗑️
                </button>
              </div>
              <div className="card-content">
                <div className="form-group">
                  <input
                    type="text"
                    value={typeof item === "string" ? item : JSON.stringify(item)}
                    onChange={(e) => {
                      const updated = [...fieldData];
                      updated[index] = e.target.value;
                      updateDynamicField(updated);
                    }}
                    placeholder={`Enter ${sectionLabel.toLowerCase()}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    // STRING handling - works for ANY string field (Additional Info, etc.)
    if (typeof fieldData === "string") {
      return (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">{sectionLabel}</h3>
          </div>
          <div className="experience-card">
            <div className="card-content">
              <div className="form-group">
                <label>{sectionLabel}</label>
                <textarea
                  value={fieldData}
                  onChange={(e) => updateDynamicField(e.target.value)}
                  placeholder={`Enter ${sectionLabel.toLowerCase()}`}
                  rows={5}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    // OBJECT handling - works for ANY object field
    if (typeof fieldData === "object" && !Array.isArray(fieldData)) {
      return (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">{sectionLabel}</h3>
          </div>
          <div className="experience-card">
            <div className="card-content">
              {Object.keys(fieldData).map((key) => (
                <div key={key} className="form-group">
                  <label>
                    {key
                      .split("_")
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ")}
                  </label>
                  <input
                    type="text"
                    value={fieldData[key] || ""}
                    onChange={(e) => {
                      updateDynamicField({ ...fieldData, [key]: e.target.value });
                    }}
                    placeholder={`Enter ${key.split("_").join(" ")}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };
  const renderForm = () => {
    const formProps = {
      resumeData,
      onUpdate: handleUpdate,
      validationErrors,
      onFieldFocus: handleFieldFocus,
      onFieldBlur: handleFieldBlur,
      // Pass dynamic section helpers to ALL forms
      getDynamicSections,
      renderDynamicSectionForm,
      templateId,
    };
    switch (templateId) {
      case "simple":
        return <SimpleResumeForm {...formProps} />;
      case "modern":
        return <ModernResumeForm {...formProps} />;
      case "security-manager":
        return <SecurityResumeForm {...formProps} />;
      case "computer-teacher":
      case "tech-resume":
        return <TechResumeForm {...formProps} />;
      case "pe-teacher":
        return <PETeacherResumeForm {...formProps} />;
      case "business-development":
        return <BusinessDevResumeForm {...formProps} />;
      case "cv-guy":
        return <ExecutiveResumeForm {...formProps} />;
      case "marketing-specialist":
        return <MarketingSpecialistResumeForm {...formProps} />;
      case "science-teacher":
        return <ScienceTeacherResumeForm {...formProps} />;
      case "english-teacher":
        return <EnglishTeacherResumeForm {...formProps} />;
      case "consulting-profile":
        return <ConsultingProfessionalResumeForm {...formProps} />;
      case "tcs":
        return <TCSResumeForm {...formProps} />;
      case "ats":
        // ATS template reuses professional layout with template-specific fields
        return <ProfessionalResumeForm {...formProps} />;
      default:
        return <ProfessionalResumeForm {...formProps} />;
    }
  };
  const renderPreview = () => {
    // Always use the selected template, but with different data based on whether tailored resume is active
    const previewData =
      showTailoredResume && tailoredResumeData
        ? tailoredResumeData
        : resumeData;
    // Use preview data directly without JD keywords
    const enhancedPreviewData = {
      ...previewData,
    };
    // Ensure the template structure is preserved, only data changes
          switch (templateId) {
      case "simple":
        return (
          <SimpleResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "modern":
        return (
          <ModernResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
            onImageUpload={handleImageUpload}
          />
        );
      case "security-manager":
        return (
          <SecurityResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "computer-teacher":
      case "tech-resume":
        return (
          <TechResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "pe-teacher":
        return (
          <PETeacherResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "business-development":
        return (
          <BusinessDevResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "cv-guy":
        return (
          <ExecutiveResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "marketing-specialist":
        return (
          <MarketingSpecialistResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "science-teacher":
        return (
          <ScienceTeacherResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "english-teacher":
        return (
          <EnglishTeacherResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "consulting-profile":
        return (
          <ConsultingProfessionalResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "tcs":
        return (
          <TCSResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
      case "ats":
        return (
          <ATSResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
        default:
        return (
          <ProfessionalResumePreview
            componentRef={componentRef}
            resumeData={enhancedPreviewData}
          />
        );
    }
  };
  // Utility function to add page break classes for better PDF formatting
  // const addPageBreakClasses = (element) => {
  //   const sections = element.querySelectorAll('.section-container, .experience-item, .education-item, .project-item');
  //   let currentHeight = 0;
  //   const pageHeight = 11 * 72; // 11 inches in points (72 points per inch)
  //   sections.forEach((section, index) => {
  //     const sectionHeight = section.offsetHeight;
  //     currentHeight += sectionHeight;
  //     // Add page break before if content would exceed page height
  //     if (currentHeight > pageHeight && index > 0) {
  //       section.classList.add('page-break-before');
  //       currentHeight = sectionHeight;
  //     }
  //     // Ensure sections don't break across pages
  //     section.classList.add('page-break-inside-avoid');
  //   });
  // };
  if (!resumeData) {
    return (
      <div className="premium-loading no-print">
        <div className="loading-spinner"></div>
        <p>Preparing your premium resume editor...</p>
      </div>
    );
  }
  return (
    <>
      {loading && (
        <div className="loading-spinner-overlay">
      <div className="loading-spinner"></div>
      <p>Preparing print...</p>
        </div>
      )}
    <div className="premium-resume-editor">
      <div className="premium-navbar no-print">
        <div className="navbar-left">
          <button onClick={onBack} className="premium-back-btn">
            <span>←</span>
            <span>Back to Templates</span>
            </button>
        </div>
        <div className="navbar-center">
          <div className="editor-info">
            <h1 className="premium-title">Resume Editor</h1>
            <p className="template-info">
                {templateId
                  .split("-")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}{" "}
                Template
            </p>
            <div className="completion-bar">
                <div
                  className="completion-fill"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              <span className="completion-text">{completionPercentage}%</span>
            </div>
          </div>
        </div>
                <div className="navbar-right">
          {/* <button onClick={() => setPreviewMode(!previewMode)} className={`premium-btn preview-btn ${previewMode ? 'active' : ''}`}>
            <span>{previewMode ? '✍️' : '👁️'}</span>
            <span>{previewMode ? "Editor" : "Preview"}</span>
            </button> */}
            <button
              onClick={handleSaveCurrentTemplate}
              className="premium-btn save-resume-btn"
            >
              <span>💾</span>
              <span>Save</span>
            </button>
            {/* Create Resume Button */}
            <button 
              onClick={() => setShowCreateResumeModal(true)}
              className="premium-btn create-resume-btn"
            >
              <span>✨</span>
              <span>Create Resume</span>
            </button>
            <button onClick={handlePrint} className="premium-btn export-btn">
            <span>📄</span>
            <span>Download PDF</span>
          </button>
          {/* <button onClick={handleTailoredResumeClick} className="premium-btn tailored-btn">
            <span>🎯</span>
            <span>Tailored Resume</span>
          </button> */}
          {externalResumeData?.resume_summary && (
              <button
                className="premium-btn summary-btn"
                onClick={() => setShowSummary(true)}
              >
              📝 Resume Summary
            </button>
          )}
          {externalResumeData?.cover_letter && (
              <button
                className="premium-btn coverletter-btn"
                onClick={() => setShowCoverLetter(true)}
              >
              📬 Cover Letter
            </button>
          )}
        </div>
      </div>
      <div className="premium-editor-content">
        <div className="premium-editor-grid">
            <div
              className={`premium-form-panel no-print ${previewMode ? "hidden-mobile" : ""}`}
            >
            {renderForm()}
          </div>
            <div
              className={`premium-preview-panel ${!previewMode ? "hidden-mobile" : ""}`}
            >
            {showTailoredResume && (
              <div className="tailored-indicator">
                <span>🎯 Tailored Resume</span>
              </div>
            )}
            {/* PRINT ROOT — this is what we print */}
              <div>{renderPreview()}</div>
            </div>
          </div>
            </div>
          </div>
        {/* JD Upload Section */}
          {showJDUpload && (
            <div className="jd-upload-overlay">
              <div className="jd-upload-modal">
                <h3>Job Description Input</h3>
            <p>
              Upload a file or paste the job description to create a tailored
              resume.
            </p>
                {/* Input Type Toggle */}
                <div className="input-type-toggle">
                  <button 
                className={`toggle-btn ${jdInputType === "file" ? "active" : ""}`}
                onClick={() => setJdInputType("file")}
                  >
                    📁 Upload File
                  </button>
                  <button 
                className={`toggle-btn ${jdInputType === "text" ? "active" : ""}`}
                onClick={() => setJdInputType("text")}
                  >
                    📝 Paste Text
                  </button>
                </div>
                {/* File Upload */}
            {jdInputType === "file" && (
                  <div className="file-upload-section">
                    <input
                      type="file"
                      onChange={(e) => handleJDUpload(e.target.files[0])}
                      accept=".pdf,.doc,.docx"
                      className="jd-file-input"
                    />
                    {jdFile && (
                      <div className="jd-file-info">
                        <p>✅ Selected file: {jdFile.name}</p>
                      </div>
                    )}
                  </div>
                )}
                {/* Text Input */}
            {jdInputType === "text" && (
                  <div className="text-input-section">
                    <textarea
                      placeholder="Paste your job description here..."
                      value={jdText}
                      onChange={(e) => handleJDTextChange(e.target.value)}
                      rows={8}
                      className="jd-textarea"
                    />
                <p className="text-hint">
                  💡 Copy and paste the job description from any source
                </p>
                  </div>
                )}
                {/* Generate Button */}
                {(jdFile || jdText.trim()) && (
                  <div className="generate-section">
                    <button 
                      className="generate-tailored-btn"
                      onClick={handleGenerateTailoredResume}
                      disabled={isGeneratingTailored}
                    >
                  {isGeneratingTailored
                    ? "Generating..."
                    : "Generate Tailored Resume"}
                    </button>
                  </div>
                )}
                {tailoredError && (
              <div className="tailored-error">{tailoredError}</div>
                )}
                <button 
                  className="close-jd-upload"
                  onClick={() => setShowJDUpload(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {/* Summary and Cover Letter Modals */}
          {showSummary && (
            <div className="ai-popup">
              <div className="ai-popup-content">
                <div className="popup-header">
                  <h3>Resume Summary</h3>
                  <button 
                    className="popup-close-btn"
                    onClick={() => setShowSummary(false)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
                <p>{externalResumeData.resume_summary}</p>
              </div>
            </div>
          )}
          {showCoverLetter && (
            <div className="ai-popup">
              <div className="ai-popup-content">
                <div className="popup-header">
                  <h3>Cover Letter</h3>
                  <button 
                    className="popup-close-btn"
                    onClick={() => setShowCoverLetter(false)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
                <pre>{externalResumeData.cover_letter}</pre>
              </div>
            </div>
          )}
      {/* Create Resume Modal */}
      {showCreateResumeModal && (
        <ResumeUploadModal
          onUpload={handleUploadResumeFromModal}
          onCreateNew={handleCreateNewResume}
          onClose={() => setShowCreateResumeModal(false)}
          showStartFresh={false}
        />
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="resume-toast-notification">
          <div className="resume-toast-content">
            <div className="resume-toast-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <span className="resume-toast-message">{toastMessage}</span>
            <button 
              className="resume-toast-close"
              onClick={() => {
                setShowToast(false);
                setTimeout(() => setToastMessage(""), 300);
              }}
            >
              ×
            </button>
          </div>
          <div className="resume-toast-progress-bar"></div>
        </div>
      )}

      <style>{`
        .resume-toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          min-width: 320px;
          max-width: 400px;
          z-index: 10000;
          overflow: hidden;
          animation: resumeToastSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          border: 1px solid #e2e8f0;
        }
        @keyframes resumeToastSlideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .resume-toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          position: relative;
        }
        .resume-toast-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #22c55e;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          animation: resumeIconBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        @keyframes resumeIconBounce {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
        .resume-toast-message {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
          line-height: 1.5;
        }
        .resume-toast-close {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .resume-toast-close:hover {
          background: #f1f5f9;
          color: #64748b;
        }
        .resume-toast-progress-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 4px;
          background: #22c55e;
          animation: resumeToastProgress 4s linear forwards;
          border-radius: 0 0 12px 12px;
        }
        @keyframes resumeToastProgress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </>
  );
};
export default ResumeEditor;
